import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useState, useCallback, Fragment } from 'react'
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  DollarSign,
  Calendar,
  User,
  TrendingUp,
  Package,
  Mail,
  Plus,
  Pencil,
  Trash2,
  X,
  Users,
  Filter,
  Video,
  ExternalLink,
  Check,
  Lock,
  FileText,
  AlertTriangle,
  Undo2,
  Loader2,
} from 'lucide-react'
import { GRACKLE_COLORS, SEMANTIC } from '@/lib/theme'
import { ClosedWonCelebration } from '@/components/ClosedWonCelebration'
import { toDisplayDate } from '@/lib/utils'
import {
  canActOnDiscount,
  canRevertDiscount,
  isFirstPassEndorsement,
  approvalTierLabel,
  showsApprovalRow,
} from '@/lib/discountApproval'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { getBantStatusVariant, type BantCriterion } from '@/lib/bant-variants'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { CurrencySelector } from '@/components/ui/currency-selector'
import { useToast } from '@/hooks/use-toast'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { EditActionBar } from '@/components/EditActionBar'
import { ActivityTimeline } from '@/components/ActivityTimeline'
import { HistoryTimeline } from '@/components/HistoryTimeline'
import { AttachmentsTab } from '@/components/AttachmentsTab'
import { RelatedEmails } from '@/components/RelatedEmails'
import { CreateOpportunityContactRoleModal } from '@/components/CreateOpportunityContactRoleModal'
import { EditOpportunityContactRoleModal } from '@/components/EditOpportunityContactRoleModal'
import { NoteDetailModal } from '@/components/NoteDetailModal'
import { EditNoteModal } from '@/components/EditNoteModal'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { useDeleteNote as useDeleteNoteMutation, useDeleteNoteSession } from '@/hooks/use-api'
import type { Note } from '@/types'
import type { NoteSession } from '@/types/noteSession'
import { CreateEventModal } from '@/components/CreateEventModal'
import { CreateContactModal } from '@/components/CreateContactModal'
import { OpportunityStageProgress } from '@/components/OpportunityStageProgress'
import { ClosedLostModal } from '@/components/ClosedLostModal'
import { DealHealthCard } from '@/components/DealHealthScore'
import { StageVelocityIndicator } from '@/components/StageVelocityIndicator'
import { WinProbabilityCard } from '@/components/WinProbability'
import { NextBestAction } from '@/components/NextBestAction'
import { ScheduleMeetDialog } from '@/components/ScheduleMeetDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiClient } from '@/lib/api'
import { AddProductsModal } from '@/components/AddProductsModal'
import { ClickToCallButton } from '@/components/ClickToCallButton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RecordCombobox } from '@/components/ui/record-combobox'
import {
  useOpportunityDetail,
  useUpdateOpportunity,
  useOpportunityLineItems,
  useCreateOpportunityLineItem,
  useUpdateOpportunityLineItem,
  useReorderOpportunityLineItems,
  useDeleteOpportunity,
  useDeleteOpportunityLineItem,
  useDecideLineItemDiscount,
  useRevertLineItemDiscount,
  useDeleteOpportunityContactRole,
  useCreateQuoteFromOpportunity,
  useSetSyncedQuote,
  useDeleteQuote,
  useProducts,
  useUsers,
  useContacts,
  useEntityContent,
  useDealHealth,
  useStageVelocity,
  useWinProbability,
  useNoteSessionsByOpportunity,
} from '@/hooks/use-api'
import { usePicklistOptions, usePicklistOptionPairs } from '@/hooks/use-picklists'
import { useDebounce } from '@/hooks/use-debounce'
import { useClickToEdit } from '@/hooks/use-click-to-edit'
import type { Opportunity, OpportunityLineItem, OpportunityContactRole } from '@/types'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { CompletenessIndicator } from '@/components/CompletenessIndicator'
import { NoteSessionPanel } from '@/components/notes/NoteSessionPanel'
import { MergedNotesList } from '@/components/notes/MergedNotesList'
import { ownerPickerOptions } from '@/lib/salesReps'
import { calculateCompleteness, getImportantFields } from '@/utils/dataQuality'
import { useUser } from '@/contexts/UserContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { explainError } from '@/lib/explain-error'

export default function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()
  const { currentUser, isAdmin, isSuperAdmin, impersonatedUser } = useUser()
  const deleteNoteMutation = useDeleteNoteMutation(id)
  const deleteSessionMutation = useDeleteNoteSession()

  const OPPORTUNITY_TYPES = usePicklistOptions('opportunity', 'type')
  const LEAD_SOURCES = usePicklistOptions('opportunity', 'lead_source')
  const OPPORTUNITY_CATEGORIES = usePicklistOptions('opportunity', 'category')
  const contractTermPairs = usePicklistOptionPairs('opportunity', 'contract_term')
  const BANT_BUDGET_OPTIONS = usePicklistOptions('opportunity', 'budget_status')
  const BANT_AUTHORITY_OPTIONS = usePicklistOptions('opportunity', 'authority_status')
  const BANT_NEED_OPTIONS = usePicklistOptions('opportunity', 'need_status')
  const BANT_TIMELINE_OPTIONS = usePicklistOptions('opportunity', 'timeline_status')

  const { data, isLoading, error } = useOpportunityDetail(id!)
  useDocumentTitle(
    data?.opportunity
      ? `${data.opportunity.name || data.opportunity.opportunity_name} | Opportunity`
      : undefined
  )
  useRecordBreadcrumb(
    data?.opportunity ? data.opportunity.name || data.opportunity.opportunity_name : undefined
  )
  const { data: lineItemsData, isLoading: lineItemsLoading } = useOpportunityLineItems(id!)
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data || []
  const { data: filesData } = useEntityContent(id!)
  const files = filesData?.data ?? []
  const isClosed = data?.opportunity?.is_closed
  const { data: dealHealth, isLoading: dealHealthLoading } = useDealHealth(id!, !isClosed)
  const { data: stageVelocity, isLoading: stageVelocityLoading } = useStageVelocity(id!, !isClosed)
  const { data: winProbability, isLoading: winProbabilityLoading } = useWinProbability(
    id!,
    !isClosed
  )
  const { data: noteSessions } = useNoteSessionsByOpportunity(id!)

  // Edit mode state for opportunity fields
  const [isEditMode, setIsEditMode] = useState(false)
  const [notifyOwner, setNotifyOwner] = useState(true)
  const [editForm, setEditForm] = useState<Partial<Opportunity>>({})
  // Snapshot of the form when edit mode opened, so Save can send only the
  // fields the user actually changed (avoids re-submitting — and re-validating
  // — stale unrelated fields that would 400 the whole update).
  const [initialEditForm, setInitialEditForm] = useState<Partial<Opportunity>>({})

  // Server-side search for dropdowns
  const [contactSearch, setContactSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const debouncedContactSearch = useDebounce(contactSearch, 300)
  const debouncedProductSearch = useDebounce(productSearch, 300)

  const { data: contactsData, isFetching: contactsLoading } = useContacts({
    search: debouncedContactSearch || undefined,
    limit: 10000,
    sort_by: 'last_name',
    sort_order: 'asc',
    account_id: data?.opportunity?.account_id || undefined,
  })
  const contacts = contactsData?.data || []
  const { data: productsData, isFetching: productsLoading } = useProducts({
    search: debouncedProductSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })

  const updateOpportunityMutation = useUpdateOpportunity()
  const createLineItemMutation = useCreateOpportunityLineItem()
  const updateLineItemMutation = useUpdateOpportunityLineItem()
  const deleteLineItemMutation = useDeleteOpportunityLineItem()
  const reorderLineItemsMutation = useReorderOpportunityLineItems(id!)

  // Discount approval mutations. Both invalidate the line-item cache rather than
  // reloading the page, and expose isPending so the row's buttons can be
  // disabled in flight (a double-click used to fire duplicate approvals and
  // duplicate notification emails).
  const decideDiscountMutation = useDecideLineItemDiscount()
  const revertDiscountMutation = useRevertLineItemDiscount()
  const discountActionInFlight =
    decideDiscountMutation.isPending || revertDiscountMutation.isPending
  // Line item whose approval the user is about to reverse (drives the confirm dialog).
  const [discountToRevert, setDiscountToRevert] = useState<OpportunityLineItem | null>(null)

  // Contact role mutations
  const deleteContactRoleMutation = useDeleteOpportunityContactRole()

  // Quote mutations
  const createQuoteFromOppMutation = useCreateQuoteFromOpportunity()
  const setSyncedQuoteMutation = useSetSyncedQuote()
  const deleteQuoteMutation = useDeleteQuote()
  const [quoteToDelete, setQuoteToDelete] = useState<{ id: string; name: string } | null>(null)

  // Delete: the owner or an Admin/Super Admin may delete; blocked while impersonating.
  // (canDelete is defined after `opportunity` is in scope — see below.)
  const deleteOpportunityMutation = useDeleteOpportunity()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Line item form state
  const [isAddingLineItem, setIsAddingLineItem] = useState(false)
  const [addProductsModalOpen, setAddProductsModalOpen] = useState(false)
  const [editingLineItemId, setEditingLineItemId] = useState<string | null>(null)
  const [lineItemForm, setLineItemForm] = useState<Partial<OpportunityLineItem>>({
    product_id: '',
    quantity: 1,
    unit_price: 0,
    discount: 0,
    discount_description: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Contact roles filter state
  const [contactRoleFilter, setContactRoleFilter] = useState<string>('all')

  // Contact role modal state
  const [createContactRoleModalOpen, setCreateContactRoleModalOpen] = useState(false)
  const [editContactRoleModalOpen, setEditContactRoleModalOpen] = useState(false)
  const [selectedContactRole, setSelectedContactRole] = useState<OpportunityContactRole | null>(
    null
  )

  // Note modal state
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)
  const [noteDetailModalOpen, setNoteDetailModalOpen] = useState(false)
  const [editingNote, setEditingNote] = useState<Note | null>(null)
  const [editNoteModalOpen, setEditNoteModalOpen] = useState(false)
  const [editingSession, setEditingSession] = useState<NoteSession | null>(null)

  // Contact modal state

  // Meet dialog state
  const [showMeetDialog, setShowMeetDialog] = useState(false)

  // Note session panel state
  const [notePanelOpen, setNotePanelOpen] = useState(false)
  const [existingSessionId, setExistingSessionId] = useState<string | undefined>(undefined)

  // Task, Event, and Contact modal state
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false)
  const [createEventModalOpen, setCreateEventModalOpen] = useState(false)
  const [createBillingContactModalOpen, setCreateBillingContactModalOpen] = useState(false)

  // Renewal opportunity created notification
  const [renewalCreated, setRenewalCreated] = useState<{ id: string; name: string } | null>(null)

  // Closed Won celebration state
  const [showCelebration, setShowCelebration] = useState(false)
  const [showClosedLostModal, setShowClosedLostModal] = useState(false)

  // Workspace renewal justification prompt (shown when the backend blocks a
  // sub-10%-increase Workspace renewal until the rep explains it).
  const [showRenewalModal, setShowRenewalModal] = useState(false)
  const [renewalJustification, setRenewalJustification] = useState('')
  const [renewalInfo, setRenewalInfo] = useState<{
    current: number
    prior: number
    pct: number | null
    currencyChanged?: boolean
    currentCurrency?: string
    priorCurrency?: string
  } | null>(null)
  // 'lineItem' = triggered while saving a line item (resubmit that line);
  // 'gate' = triggered by the quote/Closed-Won gate (record justification via endpoint).
  const [renewalContext, setRenewalContext] = useState<'lineItem' | 'gate'>('lineItem')
  const handleCelebrationComplete = useCallback(() => {
    setShowCelebration(false)
  }, [])

  // Handle stage change
  const handleStageChange = async (newStage: string) => {
    // Show modal for Closed Lost
    if (newStage === 'Closed Lost') {
      setShowClosedLostModal(true)
      return
    }

    // Validate billing contact for Closed Won
    if (newStage === 'Closed Won') {
      const billingContactId = opportunity.billing_contact__c || opportunity.billing_contact_id
      if (!billingContactId) {
        toast({
          title: 'Billing Contact Required',
          description:
            'A billing contact with email and phone is required before closing as Won. Please set a billing contact in the Additional Details section.',
          variant: 'destructive',
        })
        return
      }
    }

    try {
      await updateOpportunityMutation.mutateAsync({
        id: id!,
        data: { stage_name: newStage },
      })

      // Trigger celebration for Closed Won
      if (newStage === 'Closed Won') {
        setShowCelebration(true)

        // Check if a renewal opportunity was auto-created (has contract end date)
        if (opportunity.contract_end_date__c) {
          // Wait a moment for the backend to create the renewal, then find it
          setTimeout(async () => {
            try {
              const renewalName = `${opportunity.name || opportunity.opportunity_name || 'Opportunity'} - Renewal`
              const response = await (apiClient as any).client.get('/opportunities', {
                params: { search: renewalName, limit: 1 },
              })
              const renewal = response.data?.data?.[0]
              if (renewal) {
                setRenewalCreated({ id: renewal.id, name: renewal.name })
              }
            } catch {
              // Silently fail — renewal notification is non-critical
            }
          }, 1500)
        }
      }

      toast({
        title: 'Stage Updated',
        description: `Opportunity stage changed to ${newStage}.`,
      })
    } catch (error: any) {
      // Workspace renewal gate blocked Closed Won → prompt for justification / await approval.
      if (handleRenewalGateError(error)) return
      const errorMessage =
        error?.response?.data?.error || explainError(error, 'Failed to update stage')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleClosedLostConfirm = async (
    reason: string,
    details: string | undefined,
    marketing: import('@/components/ClosedLostModal').MarketingLostDecision
  ) => {
    try {
      await updateOpportunityMutation.mutateAsync({
        id: id!,
        data: {
          stage_name: 'Closed Lost',
          reason_for_loss__c: reason,
          lost_reason_details__c: details || '',
          // Marketing Status decision (applied to the primary contact server-side)
          marketing_decision: marketing.decision,
          marketing_recheck_date: marketing.recheckDate,
          bdr_assignee_id: marketing.bdrAssigneeId,
        },
      })

      toast({
        title: 'Opportunity Closed',
        description: `Opportunity closed as lost: ${reason}`,
      })
      setShowClosedLostModal(false)
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || explainError(error, 'Failed to update opportunity')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle contact role deletion
  const handleDeleteContactRole = async (contactRole: OpportunityContactRole) => {
    if (
      !window.confirm(
        `Are you sure you want to remove ${contactRole.contact_name || 'this contact'} from this opportunity?`
      )
    ) {
      return
    }

    try {
      await deleteContactRoleMutation.mutateAsync({
        opportunityId: id!,
        id: contactRole.id,
      })

      toast({
        title: 'Success',
        description: 'Contact role removed successfully.',
      })
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to remove contact role')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Create quote from opportunity
  const handleCreateQuoteFromOpportunity = async () => {
    if (!id) return
    try {
      const result = await createQuoteFromOppMutation.mutateAsync(id)
      toast({
        title: 'Quote created',
        description: `Quote ${result.data.quote_number} created successfully`,
      })
      navigate(`/quotes/${result.data.id}`)
    } catch (error) {
      // Workspace renewal gate blocked quoting → prompt for justification / await approval.
      if (handleRenewalGateError(error)) return
      const errorMessage =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        explainError(error, 'Failed to create quote')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Set/clear synced (live) quote
  const handleSetSyncedQuote = async (quoteId: string | null) => {
    if (!id) return
    try {
      await setSyncedQuoteMutation.mutateAsync({ opportunityId: id, synced_quote_id: quoteId })
      toast({
        title: quoteId ? 'Live quote set' : 'Live quote removed',
        description: quoteId
          ? 'Quote is now the live quote for this opportunity.'
          : 'Live quote has been removed.',
      })
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } }
      const errorMessage = axiosError?.response?.data?.error || 'Failed to update live quote'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Delete a quote (after confirmation)
  const handleDeleteQuoteConfirm = async () => {
    if (!quoteToDelete) return
    try {
      await deleteQuoteMutation.mutateAsync(quoteToDelete.id)
      toast({
        title: 'Quote deleted',
        description: `"${quoteToDelete.name}" has been deleted.`,
      })
      setQuoteToDelete(null)
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } }
      const errorMessage = axiosError?.response?.data?.error || 'Failed to delete quote'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Edit mode handlers for opportunity fields
  const handleStartEdit = () => {
    if (!data?.opportunity) return
    const opp = data.opportunity
    const form: Partial<Opportunity> = {
      opportunity_name: opp.opportunity_name || '',
      type: opp.type || '',
      lead_source: opp.lead_source || '',
      amount: opp.amount ?? undefined,
      currency_iso_code: opp.currency_iso_code || 'CAD',
      close_date: opp.close_date ? opp.close_date.split('T')[0] : '',
      next_step: opp.next_step || '',
      probability: opp.probability ?? undefined,
      discount__c: opp.discount__c ?? undefined,
      owner_id: opp.owner_id || '',
      description: opp.description || '',
      opportunity_category__c: opp.opportunity_category__c || '',
      billing_contact_id: opp.billing_contact__c || opp.billing_contact_id || '',
      billing_email: opp.billing_email || '',
      billing_contact_phone: opp.billing_contact_phone || '',
      billing_contact_street: opp.billing_contact_street || '',
      billing_contact_city: opp.billing_contact_city || '',
      billing_contact_state: opp.billing_contact_state || '',
      billing_contact_postal_code: opp.billing_contact_postal_code || '',
      billing_contact_country: opp.billing_contact_country || '',
      po_number__c: opp.po_number__c || '',
      memo__c: opp.memo__c || '',
      contract_term__c: opp.contract_term__c ?? undefined,
      contract_start_date__c: opp.contract_start_date__c
        ? opp.contract_start_date__c.split('T')[0]
        : '',
      contract_end_date__c: opp.contract_end_date__c ? opp.contract_end_date__c.split('T')[0] : '',
      reason_for_loss__c: opp.reason_for_loss__c || '',
      competitor__c: opp.competitor__c || '',
      of_renewal_product__c: opp.of_renewal_product__c ?? undefined,
      renewal_opportunity__c: opp.renewal_opportunity__c ? 'Yes' : 'No',
      lost_reason_details__c: opp.lost_reason_details__c || '',
      competitor_details__c: opp.competitor_details__c || '',
      budget__c: opp.budget__c || '',
      authority__c: opp.authority__c || '',
      need__c: opp.need__c || '',
      timeline__c: opp.timeline__c || '',
      budget_status__c: opp.budget_status__c || '',
      authority_status__c: opp.authority_status__c || '',
      need_status__c: opp.need_status__c || '',
      timeline_status__c: opp.timeline_status__c || '',
    }
    setEditForm(form)
    setInitialEditForm(form)
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    setEditForm({})
    setFormErrors({})
  }

  const { bindReadOnly } = useClickToEdit(isEditMode, handleStartEdit)

  const handleSaveEdit = async () => {
    // Validate required fields
    const errors: Record<string, string> = {}
    if (!editForm.opportunity_name?.trim()) {
      errors.opportunity_name = 'Opportunity name is required'
    }
    if (!editForm.close_date) {
      errors.close_date = 'Close date is required'
    }

    // Validate billing contact if stage is Closed Won
    const currentStage = editForm.stage_name || opportunity.stage_name || opportunity.stage
    if (currentStage === 'Closed Won') {
      const billingContactId =
        editForm.billing_contact_id ||
        opportunity.billing_contact__c ||
        opportunity.billing_contact_id
      if (!billingContactId) {
        errors.billing_contact_id = 'Billing contact is required for Closed Won'
      }
    }

    // Validate loss reason if stage is Closed Lost
    if (currentStage === 'Closed Lost' && !editForm.reason_for_loss__c) {
      errors.reason_for_loss__c = 'Reason for loss is required'
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      })
      return
    }

    try {
      // Send only the fields the user actually changed, so a pre-existing
      // invalid value in an untouched field can't fail the whole update.
      const changed: Record<string, unknown> = {}
      for (const key of Object.keys(editForm)) {
        const k = key as keyof typeof editForm
        if (editForm[k] !== initialEditForm[k]) {
          changed[key] = editForm[k]
        }
      }

      if (Object.keys(changed).length === 0) {
        // Nothing changed — just leave edit mode.
        setIsEditMode(false)
        setFormErrors({})
        return
      }

      if ('renewal_opportunity__c' in changed) {
        changed.renewal_opportunity__c =
          editForm.renewal_opportunity__c === 'Yes'
            ? 'true'
            : editForm.renewal_opportunity__c === 'No'
              ? 'false'
              : editForm.renewal_opportunity__c
      }

      // Only carry the notify flag when the owner actually changed.
      if ('owner_id' in changed) {
        changed.notify_owner = notifyOwner
      }

      await updateOpportunityMutation.mutateAsync({
        id: id!,
        data: changed as Partial<Opportunity> & { notify_owner?: boolean },
      })
      toast({
        title: 'Success',
        description: 'Opportunity updated successfully.',
      })
      setIsEditMode(false)
      setEditForm({})
      setFormErrors({})
    } catch (error: any) {
      // Prefer the backend's specific validation details (which field/rule
      // failed) over the generic "Validation failed" message.
      const data = error?.response?.data
      const errorMessage =
        (Array.isArray(data?.details) && data.details.length
          ? data.details.join(', ')
          : data?.error) || explainError(error, 'Failed to update opportunity')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const computeContractEndDate = (startDate: string, termMonths: number): string => {
    const date = new Date(startDate + 'T00:00:00')
    date.setMonth(date.getMonth() + termMonths)
    date.setDate(date.getDate() - 1)
    return date.toISOString().split('T')[0] as string
  }

  const handleEditFieldChange = (field: string, value: any) => {
    setEditForm((prev) => {
      const updated = { ...prev, [field]: value }
      // Auto-calculate contract end date
      if (field === 'contract_term__c' || field === 'contract_start_date__c') {
        const term = field === 'contract_term__c' ? value : prev.contract_term__c
        const start = field === 'contract_start_date__c' ? value : prev.contract_start_date__c
        if (term && start) {
          updated.contract_end_date__c = computeContractEndDate(start as string, term as number)
        }
      }
      // Auto-populate billing email/phone/address from selected contact (fallback to account address)
      if (field === 'billing_contact_id') {
        if (value) {
          const contact = contacts.find((c) => c.id === value)
          if (contact) {
            updated.billing_email = contact.email || ''
            updated.billing_contact_phone = contact.phone || ''
            const hasContactAddress =
              contact.mailing_street ||
              contact.mailing_city ||
              contact.mailing_state ||
              contact.mailing_postal_code ||
              contact.mailing_country
            if (hasContactAddress) {
              updated.billing_contact_street = contact.mailing_street || ''
              updated.billing_contact_city = contact.mailing_city || ''
              updated.billing_contact_state = contact.mailing_state || ''
              updated.billing_contact_postal_code = contact.mailing_postal_code || ''
              updated.billing_contact_country = contact.mailing_country || ''
            } else {
              // Fall back to account billing address
              updated.billing_contact_street = opportunity.account_billing_street || ''
              updated.billing_contact_city = opportunity.account_billing_city || ''
              updated.billing_contact_state = opportunity.account_billing_state || ''
              updated.billing_contact_postal_code = opportunity.account_billing_postal_code || ''
              updated.billing_contact_country = opportunity.account_billing_country || ''
            }
          }
        } else {
          updated.billing_email = ''
          updated.billing_contact_phone = ''
          updated.billing_contact_street = ''
          updated.billing_contact_city = ''
          updated.billing_contact_state = ''
          updated.billing_contact_postal_code = ''
          updated.billing_contact_country = ''
        }
      }
      return updated
    })
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="progressbar"
            aria-label="Loading opportunity details"
          ></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading opportunity details...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-96" role="alert">
        <p className="text-lg font-semibold text-destructive">Error loading opportunity</p>
        <p className="text-sm text-muted-foreground mt-2">
          {explainError(error, 'Opportunity not found')}
        </p>
        <Button
          onClick={() => navigate('/opportunities')}
          className="mt-4"
          aria-label="Return to opportunities list"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Opportunities
        </Button>
      </div>
    )
  }

  const { opportunity, notes, quotes, products, contact_roles } = data

  // Delete: the opportunity's owner OR an Admin/Super Admin may delete it; hidden while
  // impersonating (mutations are read-only then). The server enforces the same ownership rule.
  const canDelete = (isAdmin || currentUser?.id === opportunity.owner_id) && !impersonatedUser

  // Closed Won opportunities are locked for non-admin users
  const isClosedWonLocked =
    (opportunity.stage === 'Closed Won' || opportunity.stage_name === 'Closed Won') && !isAdmin

  const handleDeleteConfirm = async () => {
    try {
      await deleteOpportunityMutation.mutateAsync(opportunity.id)
      toast({
        title: 'Opportunity deleted',
        description: `"${opportunity.opportunity_name || opportunity.name}" has been deleted.`,
      })
      setDeleteDialogOpen(false)
      navigate('/opportunities')
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: explainError(error, 'Could not delete the opportunity.'),
        variant: 'destructive',
      })
    }
  }

  // Calculate completeness score
  const importantFields = getImportantFields('opportunity')
  const completeness = calculateCompleteness(opportunity, importantFields)

  // Filter contact roles based on selected role
  const filteredContactRoles =
    contactRoleFilter === 'all'
      ? contact_roles
      : contact_roles.filter((cr) => cr.role === contactRoleFilter)

  // Get unique roles from contact_roles for filter dropdown
  const availableRoles = Array.from(new Set(contact_roles.map((cr) => cr.role))).sort()

  const formatCurrency = (amount?: number) => {
    const currency = opportunity.currency_iso_code || 'CAD'
    if (!amount) {
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(0)
      } catch {
        return `0.00 ${currency}`
      }
    }
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
    } catch {
      // Intl throws on invalid/junk ISO codes — fall back to numeric + code.
      return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${currency}`
    }
  }

  const formatDate = (date?: string) => {
    const d = toDisplayDate(date)
    if (!d) return 'N/A'
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatPercent = (value?: number) => {
    if (value === null || value === undefined) return 'N/A'
    return `${value}%`
  }

  const formatDateTime = (date?: string) => {
    if (!date) return 'N/A'
    return (toDisplayDate(date) ?? new Date(date)).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Helper functions for calculated fields
  const calculateGrandTotal = (amount?: number, discount?: number): number => {
    if (!amount) return 0
    if (!discount) return amount
    return amount * (1 - discount / 100)
  }

  const calculateWeightedAmount = (amount?: number, probability?: number): number => {
    if (!amount || probability === null || probability === undefined) return 0
    return amount * (probability / 100)
  }

  const calculateWeightedGrandTotal = (grandTotal: number, probability?: number): number => {
    if (probability === null || probability === undefined) return 0
    return grandTotal * (probability / 100)
  }

  // Select options for editable fields (from picklists - hooks called at top of component)
  const CONTRACT_TERMS = contractTermPairs.map((p) => p.value)
  const CONTRACT_TERM_LABELS: Record<string, string> = Object.fromEntries(
    contractTermPairs.map((p) => [p.value, p.label])
  )

  // Render editable field helper
  const renderEditableField = (
    label: string,
    field: string,
    value: any,
    type: 'text' | 'number' | 'date' | 'select' | 'textarea' | 'owner' | 'contact' = 'text',
    options?: string[],
    required?: boolean
  ) => {
    if (isEditMode) {
      const fieldValue = editForm[field as keyof typeof editForm]
      return (
        <div className="py-2 border-b border-border">
          <dt className="text-sm font-medium text-muted-foreground">
            {label}
            {required && ' *'}
          </dt>
          <dd className="mt-0.5">
            {type === 'textarea' ? (
              <textarea
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? `${field}-error` : undefined}
              />
            ) : type === 'select' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? `${field}-error` : undefined}
              >
                <option value="">Select...</option>
                {options?.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : type === 'owner' ? (
              <>
                <select
                  id={field}
                  value={(fieldValue as string) || ''}
                  onChange={(e) => handleEditFieldChange(field, e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-invalid={!!formErrors[field]}
                  aria-describedby={formErrors[field] ? `${field}-error` : undefined}
                >
                  <option value="">Select owner...</option>
                  {ownerPickerOptions(users, opportunity?.owner_id).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    id={`${field}-notify`}
                    checked={notifyOwner}
                    onCheckedChange={(checked) => setNotifyOwner(checked === true)}
                  />
                  <Label htmlFor={`${field}-notify`} className="text-sm font-normal cursor-pointer">
                    Notify new owner by email (only if owner changes)
                  </Label>
                </div>
              </>
            ) : type === 'contact' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? `${field}-error` : undefined}
              >
                <option value="">Select contact...</option>
                {[...contacts]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.name}
                    </option>
                  ))}
              </select>
            ) : type === 'number' ? (
              <Input
                id={field}
                type="number"
                value={(fieldValue as number | string) ?? ''}
                onChange={(e) =>
                  handleEditFieldChange(
                    field,
                    e.target.value ? parseFloat(e.target.value) : undefined
                  )
                }
                className="h-9"
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? `${field}-error` : undefined}
              />
            ) : type === 'date' ? (
              <DatePicker
                id={field}
                value={fieldValue as string}
                onChange={(v) => handleEditFieldChange(field, v ?? '')}
                className="h-9"
                aria-required={required}
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? `${field}-error` : undefined}
              />
            ) : (
              <Input
                id={field}
                type="text"
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="h-9"
                aria-required={required}
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? `${field}-error` : undefined}
              />
            )}
            {formErrors[field] && (
              <p id={`${field}-error`} className="text-sm text-destructive mt-1" role="alert">
                {formErrors[field]}
              </p>
            )}
          </dd>
        </div>
      )
    }
    // Display mode — clicking the value drops the page into edit mode
    // and focuses this field's input.
    const readProps = bindReadOnly(field, label)
    return (
      <div className="py-2 border-b border-border">
        <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
        <dd
          {...readProps}
          className={`mt-0.5 text-sm flex items-center justify-between gap-2 ${readProps.className}`}
        >
          <span className="min-w-0 break-words whitespace-pre-wrap">{value}</span>
          <Pencil className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
        </dd>
      </div>
    )
  }

  // Line item data
  const lineItems = lineItemsData || []
  const availableProducts = productsData?.data || []

  // Calculate line item totals
  const calculateLineItemTotal = (quantity: number, unitPrice: number, discount: number = 0) => {
    const subtotal = quantity * unitPrice
    return subtotal - discount
  }

  const lineItemsSubtotal = lineItems.reduce((sum, item) => sum + (item.total_price || 0), 0)

  // Pending/endorsed discount approvals on this opportunity (drives the header
  // banner + the in-row controls). Two-stage: ≤10% needs an Admin; >10% gets a
  // first-pass endorsement by an Admin (Jason), then final approval by a Super
  // Admin. The server enforces all of this; the UI mirrors it.
  // Items awaiting approval — a discount OR a Workspace renewal under 10% can put
  // a line into pending/endorsed (a renewal may have no discount at all).
  const pendingDiscountItems = lineItems.filter(
    (i: any) => i.discount_status === 'pending' || i.discount_status === 'endorsed'
  )
  // The authority rules themselves live in @/lib/discountApproval, mirroring the
  // server, so they can be unit-tested without mounting this page. These are the
  // thin bindings that supply the current viewer.
  const viewer = { isAdmin, isSuperAdmin, isImpersonating: !!impersonatedUser }
  const canActOnItem = (i: any) => canActOnDiscount(i, viewer)
  const canRevertItem = (i: any) => canRevertDiscount(i, viewer)
  const isFirstPass = (i: any) => isFirstPassEndorsement(i, viewer)
  const singlePending = pendingDiscountItems.length === 1 ? pendingDiscountItems[0] : null
  const canApproveSingle = singlePending ? canActOnItem(singlePending) : false

  // Notification delivery problems are surfaced, never swallowed.
  const surfaceDiscountWarnings = (warnings?: string[]) => {
    if (!warnings?.length) return
    toast({
      title: 'Some notifications were delayed',
      description: `${warnings.join('; ')}. They have been queued for retry.`,
    })
  }

  // Shared approve/reject handler (used by the header banner and the in-row
  // buttons). The server decides endorse-vs-finalize; we toast based on the
  // returned status and surface any notification-delivery warnings.
  const handleApproveDiscount = async (itemId: string, action: 'approve' | 'reject') => {
    try {
      const result = await decideDiscountMutation.mutateAsync({
        opportunityId: id!,
        id: itemId,
        action,
      })
      toast({
        title:
          result.status === 'endorsed'
            ? 'Discount endorsed'
            : result.status === 'approved'
              ? 'Discount Approved'
              : 'Discount Rejected',
        description:
          result.status === 'endorsed' ? 'Sent to a Super Admin for final approval.' : undefined,
      })
      surfaceDiscountWarnings(result.warnings)
    } catch (err: any) {
      toast({
        title: `Could not ${action} the discount`,
        description:
          err?.response?.data?.error || explainError(err, `Failed to ${action} the discount.`),
        variant: 'destructive',
      })
    }
  }

  // Reverse a decision (approved / endorsed / rejected) back to pending. Confirmed
  // via the dialog below, since it re-opens the line for approval and re-notifies
  // the approvers.
  const handleRevertDiscount = async (item: OpportunityLineItem) => {
    const wasRejected = item.discount_status === 'rejected'
    try {
      const result = await revertDiscountMutation.mutateAsync({
        opportunityId: id!,
        id: item.id,
      })
      setDiscountToRevert(null)
      toast({
        title: wasRejected ? 'Rejection reversed' : 'Approval reversed',
        description: wasRejected
          ? 'The line is awaiting approval again. Re-enter the discount amount before quoting.'
          : 'The line is awaiting approval again and the approvers have been notified.',
      })
      surfaceDiscountWarnings(result.warnings)
    } catch (err: any) {
      toast({
        title: 'Could not reverse the decision',
        description:
          err?.response?.data?.error ||
          explainError(err, 'Failed to reverse the discount decision.'),
        variant: 'destructive',
      })
    }
  }

  // Use line items total if products exist, otherwise use stored amount
  const displayAmount = lineItems.length > 0 ? lineItemsSubtotal : opportunity.amount || 0

  // Line item form handlers
  const resetLineItemForm = () => {
    setLineItemForm({
      product_id: '',
      quantity: 1,
      unit_price: 0,
      discount: 0,
      discount_description: '',
    })
    setFormErrors({})
    setIsAddingLineItem(false)
    setEditingLineItemId(null)
  }

  const handleProductSelect = (productId: string) => {
    const product = availableProducts.find((p) => p.id === productId)
    if (product) {
      setLineItemForm((prev) => ({
        ...prev,
        product_id: productId,
        unit_price: product.unit_price || 0,
      }))
      if (formErrors.product_id) {
        setFormErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors.product_id
          return newErrors
        })
      }
    }
  }

  const validateLineItemForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!lineItemForm.product_id) {
      errors.product_id = 'Product is required'
    }

    if (!lineItemForm.quantity || lineItemForm.quantity <= 0) {
      errors.quantity = 'Quantity must be greater than 0'
    }

    if (lineItemForm.unit_price === undefined || lineItemForm.unit_price < 0) {
      errors.unit_price = 'Unit price must be 0 or greater'
    }

    if (lineItemForm.discount !== undefined && lineItemForm.discount < 0) {
      errors.discount = 'Discount must be 0 or greater'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveLineItem = async (renewalJustificationOverride?: string) => {
    if (!validateLineItemForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form',
        variant: 'destructive',
      })
      return
    }

    const lineItemData: Record<string, unknown> = {
      ...lineItemForm,
      quantity: Number(lineItemForm.quantity),
      unit_price: Number(lineItemForm.unit_price),
      discount: Number(lineItemForm.discount || 0),
    }
    const justification = (renewalJustificationOverride ?? '').trim()
    if (justification) lineItemData.renewal_justification = justification

    try {
      if (editingLineItemId) {
        await updateLineItemMutation.mutateAsync({
          opportunityId: id!,
          id: editingLineItemId,
          data: lineItemData,
        })
        toast({
          title: 'Success',
          description: 'Line item updated successfully',
        })
      } else {
        await createLineItemMutation.mutateAsync({
          opportunityId: id!,
          data: lineItemData,
        })
        toast({
          title: 'Success',
          description: 'Line item added successfully',
        })
      }
      resetLineItemForm()
      // Clear any renewal-justification prompt on success.
      setShowRenewalModal(false)
      setRenewalJustification('')
      setRenewalInfo(null)
    } catch (error) {
      const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data
      // Backend blocks a sub-10%-increase Workspace renewal until justified — open
      // the justification prompt instead of a generic error toast.
      if (data?.code === 'RENEWAL_JUSTIFICATION_REQUIRED') {
        setRenewalContext('lineItem')
        setRenewalInfo({
          current: Number(data.current_total) || 0,
          prior: Number(data.prior_total) || 0,
          pct: data.increase_pct === null ? null : Number(data.increase_pct),
          currencyChanged: Boolean(data.currency_changed),
          currentCurrency: (data.current_currency as string) || undefined,
          priorCurrency: (data.prior_currency as string) || undefined,
        })
        setShowRenewalModal(true)
        return
      }
      const message =
        (typeof data?.error === 'string' && data.error) ||
        explainError(error, 'Failed to save line item')
      toast({ title: 'Error', description: message, variant: 'destructive' })
    }
  }

  // Handle a RENEWAL_APPROVAL_REQUIRED 400 from the quote / Closed-Won gates: if a
  // justification is still needed, open the prompt (gate mode); otherwise it's already
  // pending — tell the user it's awaiting approval. Returns true if it handled the error.
  const handleRenewalGateError = (err: unknown): boolean => {
    const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
    if (data?.code !== 'RENEWAL_APPROVAL_REQUIRED') return false
    if (data.needs_justification) {
      setRenewalContext('gate')
      setRenewalInfo({
        current: Number(data.current_total) || 0,
        prior: Number(data.prior_total) || 0,
        pct: data.increase_pct === null ? null : Number(data.increase_pct),
        currencyChanged: Boolean(data.currency_changed),
        currentCurrency: (data.current_currency as string) || undefined,
        priorCurrency: (data.prior_currency as string) || undefined,
      })
      setShowRenewalModal(true)
    } else {
      toast({
        title: 'Awaiting approval',
        description:
          (typeof data.error === 'string' && data.error) ||
          'This Workspace renewal is awaiting approval and cannot proceed yet.',
        variant: 'destructive',
      })
    }
    return true
  }

  const handleConfirmRenewalJustification = async () => {
    const j = renewalJustification.trim()
    if (!j) {
      toast({
        title: 'Justification required',
        description: 'Please explain why a 10% increase is not possible.',
        variant: 'destructive',
      })
      return
    }
    if (renewalContext === 'gate') {
      // No specific line in hand — record on the opportunity's primary Workspace line.
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (apiClient as any).client.post(`/opportunities/${id}/renewal-justification`, {
          justification: j,
        })
        setShowRenewalModal(false)
        setRenewalJustification('')
        setRenewalInfo(null)
        toast({
          title: 'Sent for approval',
          description: 'You can quote or close this renewal once it has been approved.',
        })
      } catch (err) {
        toast({
          title: 'Error',
          description:
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Failed to submit justification',
          variant: 'destructive',
        })
      }
      return
    }
    await handleSaveLineItem(j)
  }

  const handleEditLineItem = (item: OpportunityLineItem) => {
    setLineItemForm({
      product_id: item.product_id || '',
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount: item.discount || 0,
      description: item.description || '',
      discount_description: item.discount_description || '',
    })
    setEditingLineItemId(item.id)
    setIsAddingLineItem(true)
  }

  const handleMoveLineItem = async (index: number, direction: 'up' | 'down') => {
    const swapIdx = direction === 'up' ? index - 1 : index + 1
    if (swapIdx < 0 || swapIdx >= lineItems.length) return
    // Reindex all items 0..n, swapping the moved item with its neighbor.
    const order = lineItems.map((it: OpportunityLineItem, i: number) => ({
      id: it.id,
      sort_order: i === index ? swapIdx : i === swapIdx ? index : i,
    }))
    try {
      await reorderLineItemsMutation.mutateAsync(order)
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to reorder products'),
        variant: 'destructive',
      })
    }
  }

  const handleDeleteLineItem = async (itemId: string) => {
    if (!confirm('Are you sure you want to delete this line item?')) {
      return
    }

    try {
      await deleteLineItemMutation.mutateAsync({
        opportunityId: id!,
        id: itemId,
      })
      toast({
        title: 'Success',
        description: 'Line item deleted successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to delete line item'),
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6" role="main" aria-label="Opportunity details">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/opportunities')}
            aria-label="Back to opportunities list"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <CategoryBadge type="opportunity" />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">
                {opportunity.opportunity_name || 'Opportunity'}
              </h1>
              <CompletenessIndicator percentage={completeness} showLabel />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {opportunity.account_name ? (
                <Link to={`/accounts/${opportunity.account_id}`} className="hover:underline">
                  {opportunity.account_name}
                </Link>
              ) : (
                'No account'
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <Button variant="outline" onClick={handleCancelEdit} aria-label="Cancel editing">
              Cancel
            </Button>
          ) : (
            <>
              <Button
                variant="default"
                onClick={() => setNotePanelOpen(true)}
                disabled={!!impersonatedUser}
                aria-label={
                  impersonatedUser
                    ? 'Read-only while impersonating'
                    : `Open call notes for ${opportunity.opportunity_name}`
                }
                title={impersonatedUser ? 'Read-only while impersonating' : undefined}
              >
                <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
                Call Notes
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowMeetDialog(true)}
                aria-label="Schedule Google Meet"
              >
                <Video className="h-4 w-4 mr-2" aria-hidden="true" />
                Schedule Meeting
              </Button>
              {!isClosedWonLocked && (
                <Button variant="outline" onClick={handleStartEdit} aria-label="Edit opportunity">
                  <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                  Edit
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  aria-label={`Delete opportunity ${opportunity.opportunity_name}`}
                >
                  <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                  Delete
                </Button>
              )}
            </>
          )}
          {opportunity.stage_name === 'Closed Won' && <Badge variant="success">Won</Badge>}
          {opportunity.is_closed && opportunity.stage_name !== 'Closed Won' && (
            <Badge variant="destructive">Closed</Badge>
          )}
        </div>
      </div>

      {/* Stage Progress */}
      <OpportunityStageProgress
        currentStage={opportunity.stage}
        onStageChange={handleStageChange}
        disabled={updateOpportunityMutation.isPending || isClosedWonLocked}
      />

      {/* Pending discount approval banner — prominent so admins act on the notification */}
      {isAdmin && pendingDiscountItems.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm"
          style={{
            backgroundColor: SEMANTIC.warning.fill,
            color: SEMANTIC.warning.text,
            borderColor: GRACKLE_COLORS.pollen,
          }}
          role="status"
        >
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {pendingDiscountItems.length === 1
              ? 'This opportunity has a discount pending approval.'
              : `This opportunity has ${pendingDiscountItems.length} discounts pending approval.`}
            {pendingDiscountItems.some((i: any) => i.discount_status === 'endorsed') &&
              !isSuperAdmin && (
                <span className="font-medium">Awaiting Super Admin final approval.</span>
              )}
          </span>
          <div className="flex flex-shrink-0 gap-2">
            {singlePending && canApproveSingle ? (
              <>
                <Button
                  size="sm"
                  className="h-7"
                  onClick={() => handleApproveDiscount(singlePending.id, 'approve')}
                  disabled={discountActionInFlight}
                  aria-label={
                    isFirstPass(singlePending)
                      ? 'Endorse pending discount'
                      : 'Approve pending discount'
                  }
                >
                  <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                  {isFirstPass(singlePending) ? 'Endorse' : 'Approve'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  onClick={() => handleApproveDiscount(singlePending.id, 'reject')}
                  disabled={discountActionInFlight}
                  aria-label="Reject pending discount"
                >
                  Reject
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                onClick={() =>
                  setSearchParams(
                    (prev) => {
                      prev.set('tab', 'products')
                      return prev
                    },
                    { replace: true }
                  )
                }
                aria-label="Review pending discounts in the Products tab"
              >
                Review in Products →
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Closed Won lock banner */}
      {isClosedWonLocked && (
        <div
          className="flex items-center gap-2 rounded-lg border px-4 py-3 text-sm"
          style={{
            backgroundColor: SEMANTIC.warning.fill,
            color: SEMANTIC.warning.text,
            borderColor: GRACKLE_COLORS.pollen,
          }}
          role="status"
        >
          <Lock className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>This Closed Won opportunity is locked. Only administrators can make changes.</span>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Amount</CardTitle>
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{formatCurrency(displayAmount)}</div>
            {lineItems.length > 0 && (
              <p className="text-xs text-muted-foreground leading-tight">
                From {lineItems.length} product{lineItems.length !== 1 ? 's' : ''}
              </p>
            )}
            {!lineItems.length &&
              opportunity.expected_revenue &&
              opportunity.expected_revenue !== opportunity.amount && (
                <p className="text-xs text-muted-foreground leading-tight">
                  Expected: {formatCurrency(opportunity.expected_revenue)}
                </p>
              )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Close Date</CardTitle>
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatDate(opportunity.close_date)}
            </div>
            {opportunity.fiscal_quarter && opportunity.fiscal_year && (
              <p className="text-xs text-muted-foreground leading-tight">
                Q{opportunity.fiscal_quarter} FY{opportunity.fiscal_year}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Probability</CardTitle>
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatPercent(opportunity.probability)}
            </div>
            {opportunity.forecast_category && (
              <p className="text-xs text-muted-foreground leading-tight">
                {opportunity.forecast_category}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Owner</CardTitle>
            <User className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-base font-semibold">{opportunity.owner_name || 'Unassigned'}</div>
          </CardContent>
        </Card>

        {!opportunity.is_closed && (
          <>
            <Card>
              <WinProbabilityCard
                winProbability={winProbability}
                isLoading={winProbabilityLoading}
              />
            </Card>
            <Card>
              <DealHealthCard health={dealHealth} isLoading={dealHealthLoading} />
            </Card>
          </>
        )}
      </div>

      {!opportunity.is_closed && (
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            {dealHealth && <NextBestAction type="opportunity" health={dealHealth} />}
          </div>
          <div className="flex-shrink-0">
            <StageVelocityIndicator velocity={stageVelocity} isLoading={stageVelocityLoading} />
          </div>
        </div>
      )}

      {/* Main Content with Sidebar Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[13fr_7fr] gap-6">
        {/* Left Column: Main Content */}
        <div className="space-y-6">
          {/* About Section */}
          <Card>
            <CardHeader>
              <CardTitle>About</CardTitle>
              <CardDescription>Key opportunity information and details</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
                {/* Column 1 */}
                {renderEditableField(
                  'Opportunity Name',
                  'opportunity_name',
                  opportunity.opportunity_name || 'N/A',
                  'text',
                  undefined,
                  true
                )}

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Account Name</dt>
                  <dd className="mt-0.5 text-sm">
                    {opportunity.account_id ? (
                      <Link
                        to={`/accounts/${opportunity.account_id}`}
                        className="text-primary hover:underline"
                      >
                        {opportunity.account_name || 'N/A'}
                      </Link>
                    ) : (
                      opportunity.account_name || 'N/A'
                    )}
                  </dd>
                </div>

                {renderEditableField(
                  'Type',
                  'type',
                  opportunity.type || 'N/A',
                  'select',
                  OPPORTUNITY_TYPES
                )}

                {renderEditableField(
                  'Category',
                  'opportunity_category__c',
                  opportunity.opportunity_category__c || 'N/A',
                  'select',
                  OPPORTUNITY_CATEGORIES
                )}

                {renderEditableField(
                  'Lead Source',
                  'lead_source',
                  opportunity.lead_source || 'N/A',
                  'select',
                  LEAD_SOURCES
                )}

                {renderEditableField(
                  'Amount',
                  'amount',
                  formatCurrency(opportunity.amount),
                  'number'
                )}

                {/* Currency (per-opp override; defaults from the account on create) */}
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Currency</dt>
                  <dd className="mt-0.5">
                    {isEditMode ? (
                      <>
                        <CurrencySelector
                          id="opportunity-currency"
                          hideOther
                          value={(editForm.currency_iso_code as string) || 'CAD'}
                          onValueChange={(v) => handleEditFieldChange('currency_iso_code', v)}
                        />
                        {editForm.amount != null &&
                          editForm.currency_iso_code !==
                            (opportunity.currency_iso_code || 'CAD') && (
                            <p
                              className="mt-1 text-xs"
                              style={{ color: GRACKLE_COLORS.prussianBlue }}
                              role="status"
                            >
                              The amount is relabeled in the new currency, not converted.
                            </p>
                          )}
                      </>
                    ) : (
                      opportunity.currency_iso_code || 'CAD'
                    )}
                  </dd>
                </div>

                {renderEditableField(
                  'Close Date',
                  'close_date',
                  formatDate(opportunity.close_date),
                  'date',
                  undefined,
                  true
                )}

                {renderEditableField(
                  'Next Step',
                  'next_step',
                  opportunity.next_step || 'N/A',
                  'text'
                )}

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Stage</dt>
                  <dd className="mt-0.5 text-sm">{opportunity.stage || 'N/A'}</dd>
                </div>

                {renderEditableField(
                  'Opportunity Owner',
                  'owner_id',
                  opportunity.owner_name || 'N/A',
                  'owner'
                )}

                {renderEditableField(
                  'Probability (%)',
                  'probability',
                  formatPercent(opportunity.probability),
                  'number'
                )}

                {renderEditableField(
                  'Discount (%)',
                  'discount__c',
                  opportunity.discount__c ? `${opportunity.discount__c}%` : 'N/A',
                  'number'
                )}

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Renewal Opportunity</dt>
                  <dd className="mt-0.5 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="renewal-opportunity-toggle"
                      checked={
                        isEditMode
                          ? editForm.renewal_opportunity__c === 'Yes'
                          : !!opportunity.renewal_opportunity__c
                      }
                      onChange={async (e) => {
                        if (isEditMode) {
                          handleEditFieldChange(
                            'renewal_opportunity__c',
                            e.target.checked ? 'Yes' : 'No'
                          )
                        } else {
                          try {
                            await updateOpportunityMutation.mutateAsync({
                              id: id!,
                              data: { renewal_opportunity__c: e.target.checked ? 'true' : 'false' },
                            })
                            toast({
                              title: 'Updated',
                              description: `Renewal opportunity ${e.target.checked ? 'enabled' : 'disabled'}.`,
                            })
                          } catch {
                            toast({
                              title: 'Error',
                              description: 'Failed to update.',
                              variant: 'destructive',
                            })
                          }
                        }
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      aria-label="Toggle renewal opportunity"
                    />
                    <label htmlFor="renewal-opportunity-toggle" className="text-sm cursor-pointer">
                      {isEditMode
                        ? editForm.renewal_opportunity__c === 'Yes'
                          ? 'Yes'
                          : 'No'
                        : opportunity.renewal_opportunity__c
                          ? 'Yes'
                          : 'No'}
                    </label>
                  </dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Grand Total</dt>
                  <dd className="mt-0.5 text-sm font-semibold">
                    {formatCurrency(calculateGrandTotal(displayAmount, opportunity.discount__c))}
                  </dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Weighted Amount</dt>
                  <dd className="mt-0.5 text-sm font-semibold">
                    {formatCurrency(
                      calculateWeightedAmount(displayAmount, opportunity.probability)
                    )}
                  </dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">
                    Weighted Grand Total
                  </dt>
                  <dd className="mt-0.5 text-sm font-semibold">
                    {formatCurrency(
                      calculateWeightedGrandTotal(
                        calculateGrandTotal(displayAmount, opportunity.discount__c),
                        opportunity.probability
                      )
                    )}
                  </dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Campaign</dt>
                  <dd className="mt-0.5 text-sm">{opportunity.campaign_name || 'N/A'}</dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Private</dt>
                  <dd className="mt-0.5 text-sm">{opportunity.is_private ? 'Yes' : 'No'}</dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Fiscal Period</dt>
                  <dd className="mt-0.5 text-sm">
                    {opportunity.fiscal_quarter && opportunity.fiscal_year
                      ? `Q${opportunity.fiscal_quarter} FY${opportunity.fiscal_year}`
                      : 'N/A'}
                  </dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Forecast Category</dt>
                  <dd className="mt-0.5 text-sm">{opportunity.forecast_category || 'N/A'}</dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Quote Name</dt>
                  <dd className="mt-0.5 text-sm">
                    {quotes.length > 0 && quotes[0]?.quote_name ? quotes[0]?.quote_name : 'N/A'}
                  </dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Contract Name</dt>
                  <dd className="mt-0.5 text-sm">{opportunity.contract_id || 'N/A'}</dd>
                </div>

                <div className="md:col-span-2 py-2 border-b border-border">
                  {isEditMode ? (
                    <>
                      <dt className="text-sm font-medium text-muted-foreground">Description</dt>
                      <dd className="mt-0.5">
                        <textarea
                          value={editForm.description || ''}
                          onChange={(e) => handleEditFieldChange('description', e.target.value)}
                          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label="Description"
                        />
                      </dd>
                    </>
                  ) : (
                    <>
                      <dt className="text-sm font-medium text-muted-foreground">Description</dt>
                      <dd className="mt-0.5 text-sm whitespace-pre-wrap">
                        {opportunity.description || 'N/A'}
                      </dd>
                    </>
                  )}
                </div>
              </dl>
              {isEditMode && (
                <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={updateOpportunityMutation.isPending}
                    aria-label="Cancel editing"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    disabled={updateOpportunityMutation.isPending}
                    aria-label="Save opportunity changes"
                  >
                    {updateOpportunityMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* BANT Qualification Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>BANT Qualification</CardTitle>
                <CardDescription>
                  Budget, Authority, Need, Timeline — qualify the opportunity
                </CardDescription>
              </div>
              {!isEditMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartEdit}
                  aria-label="Edit BANT qualification"
                >
                  <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(
                  [
                    {
                      key: 'budget__c',
                      statusKey: 'budget_status__c',
                      criterion: 'budget' as BantCriterion,
                      label: 'Budget',
                      help: "Prospect's financial readiness",
                      placeholder: 'Confirmed budget, approval status, range, etc.',
                      options: BANT_BUDGET_OPTIONS,
                    },
                    {
                      key: 'authority__c',
                      statusKey: 'authority_status__c',
                      criterion: 'authority' as BantCriterion,
                      label: 'Authority',
                      help: 'Decision-maker / approval path',
                      placeholder: 'Who signs? Other stakeholders involved?',
                      options: BANT_AUTHORITY_OPTIONS,
                    },
                    {
                      key: 'need__c',
                      statusKey: 'need_status__c',
                      criterion: 'need' as BantCriterion,
                      label: 'Need',
                      help: 'Pain point or business driver',
                      placeholder: 'What problem does this solve?',
                      options: BANT_NEED_OPTIONS,
                    },
                    {
                      key: 'timeline__c',
                      statusKey: 'timeline_status__c',
                      criterion: 'timeline' as BantCriterion,
                      label: 'Timeline',
                      help: 'Decision and purchase timing',
                      placeholder: 'Target decision date, urgency, blockers',
                      options: BANT_TIMELINE_OPTIONS,
                    },
                  ] as const
                ).map(({ key, statusKey, criterion, label, help, placeholder, options }) => {
                  const statusValue = (opportunity as any)[statusKey] as string | undefined
                  const notesValue = (opportunity as any)[key] as string | undefined
                  return (
                    <div key={key} className="py-2 border-b border-border">
                      <dt className="text-sm font-semibold text-foreground mb-2">{label}</dt>
                      {isEditMode ? (
                        <dd className="space-y-2">
                          <div>
                            <label
                              htmlFor={`bant-${statusKey}`}
                              className="text-xs font-medium text-muted-foreground block mb-1"
                            >
                              Status
                            </label>
                            <select
                              id={`bant-${statusKey}`}
                              value={(editForm as any)[statusKey] || ''}
                              onChange={(e) => handleEditFieldChange(statusKey, e.target.value)}
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              aria-label={`${label} status — ${help}`}
                            >
                              <option value="">Select...</option>
                              {options.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label
                              htmlFor={`bant-${key}`}
                              className="text-xs font-medium text-muted-foreground block mb-1"
                            >
                              Notes
                            </label>
                            <textarea
                              id={`bant-${key}`}
                              value={(editForm as any)[key] || ''}
                              onChange={(e) => handleEditFieldChange(key, e.target.value)}
                              placeholder={placeholder}
                              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              aria-label={`${label} notes`}
                            />
                          </div>
                        </dd>
                      ) : (
                        <dd className="space-y-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-xs font-medium text-muted-foreground">
                              Status:
                            </span>
                            {statusValue ? (
                              <Badge variant={getBantStatusVariant(criterion, statusValue)}>
                                {statusValue}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">Not set</span>
                            )}
                          </div>
                          <div>
                            <span className="text-xs font-medium text-muted-foreground">
                              Notes:{' '}
                            </span>
                            <span className="text-sm whitespace-pre-wrap">
                              {notesValue || 'N/A'}
                            </span>
                          </div>
                        </dd>
                      )}
                    </div>
                  )
                })}
              </dl>
              {isEditMode && (
                <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={updateOpportunityMutation.isPending}
                    aria-label="Cancel editing"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    disabled={updateOpportunityMutation.isPending}
                    aria-label="Save BANT qualification"
                  >
                    {updateOpportunityMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Details Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Additional Details</CardTitle>
                <CardDescription>Extended opportunity information and metadata</CardDescription>
              </div>
              {isEditMode ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  aria-label="Cancel editing"
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartEdit}
                  aria-label="Edit additional details"
                >
                  <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
                {/* Billing Contact - searchable combobox in edit mode, link in display mode */}
                {isEditMode ? (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Billing Contact</dt>
                    <dd className="mt-0.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <RecordCombobox
                            id="billing-contact"
                            records={contacts}
                            value={editForm.billing_contact_id || ''}
                            onValueChange={(val) =>
                              handleEditFieldChange('billing_contact_id', val)
                            }
                            placeholder="Select billing contact"
                            searchPlaceholder="Search contacts..."
                            emptyMessage="No contacts found."
                            displayField={(c) =>
                              `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.name || ''
                            }
                            aria-label="Billing contact"
                            onSearchChange={setContactSearch}
                            isLoading={contactsLoading}
                            selectedLabel={data?.opportunity?.billing_contact_name || undefined}
                            totalCount={contactsData?.total}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCreateBillingContactModalOpen(true)}
                          aria-label="Create new billing contact"
                          className="shrink-0"
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                      {formErrors.billing_contact_id && (
                        <p className="text-sm text-destructive mt-1" role="alert">
                          {formErrors.billing_contact_id}
                        </p>
                      )}
                    </dd>
                  </div>
                ) : (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Billing Contact</dt>
                    <dd className="mt-0.5 text-sm">
                      {opportunity.billing_contact__c || opportunity.billing_contact_id ? (
                        <Link
                          to={`/contacts/${opportunity.billing_contact__c || opportunity.billing_contact_id}`}
                          className="text-primary hover:underline text-sm"
                          aria-label={`View contact: ${opportunity.billing_contact_name}`}
                        >
                          {opportunity.billing_contact_name || 'N/A'}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </dd>
                  </div>
                )}

                {/* Billing Email - special handling for mailto link in display mode */}
                {isEditMode ? (
                  renderEditableField(
                    'Billing Email',
                    'billing_email',
                    opportunity.billing_email || 'N/A',
                    'text'
                  )
                ) : (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Billing Email</dt>
                    <dd className="mt-0.5 text-sm">
                      {opportunity.billing_email ? (
                        <a
                          href={`mailto:${opportunity.billing_email}`}
                          className="text-primary hover:underline"
                        >
                          {opportunity.billing_email}
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </dd>
                  </div>
                )}

                {/* Billing Phone - editable, saves to billing contact record */}
                {isEditMode ? (
                  renderEditableField(
                    'Billing Phone',
                    'billing_contact_phone',
                    opportunity.billing_contact_phone || 'N/A',
                    'text'
                  )
                ) : (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Billing Phone</dt>
                    <dd className="mt-0.5 text-sm">
                      {opportunity.billing_contact_phone ? (
                        <ClickToCallButton
                          phoneNumber={opportunity.billing_contact_phone}
                          whoId={opportunity.billing_contact_id}
                          whoType="Contact"
                          whoName={opportunity.billing_contact_name}
                          whatId={opportunity.id}
                          whatType="Opportunity"
                        />
                      ) : (
                        'N/A'
                      )}
                    </dd>
                  </div>
                )}

                {/* Billing Address - editable, saves to billing contact record */}
                {isEditMode ? (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Billing Address</dt>
                    <dd className="mt-0.5 space-y-2">
                      <input
                        type="text"
                        value={editForm.billing_contact_street || ''}
                        onChange={(e) =>
                          handleEditFieldChange('billing_contact_street', e.target.value)
                        }
                        placeholder="Street"
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        aria-label="Billing street address"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={editForm.billing_contact_city || ''}
                          onChange={(e) =>
                            handleEditFieldChange('billing_contact_city', e.target.value)
                          }
                          placeholder="City"
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label="Billing city"
                        />
                        <input
                          type="text"
                          value={editForm.billing_contact_state || ''}
                          onChange={(e) =>
                            handleEditFieldChange('billing_contact_state', e.target.value)
                          }
                          placeholder="State/Province"
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label="Billing state or province"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={editForm.billing_contact_postal_code || ''}
                          onChange={(e) =>
                            handleEditFieldChange('billing_contact_postal_code', e.target.value)
                          }
                          placeholder="Postal Code"
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label="Billing postal code"
                        />
                        <input
                          type="text"
                          value={editForm.billing_contact_country || ''}
                          onChange={(e) =>
                            handleEditFieldChange('billing_contact_country', e.target.value)
                          }
                          placeholder="Country"
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label="Billing country"
                        />
                      </div>
                    </dd>
                  </div>
                ) : (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Billing Address</dt>
                    <dd className="mt-0.5 text-sm">
                      {opportunity.billing_contact_street ||
                      opportunity.billing_contact_city ||
                      opportunity.billing_contact_state ||
                      opportunity.billing_contact_postal_code ||
                      opportunity.billing_contact_country ? (
                        <address className="not-italic">
                          {opportunity.billing_contact_street && (
                            <span>
                              {opportunity.billing_contact_street}
                              <br />
                            </span>
                          )}
                          {(opportunity.billing_contact_city ||
                            opportunity.billing_contact_state ||
                            opportunity.billing_contact_postal_code) && (
                            <span>
                              {[opportunity.billing_contact_city, opportunity.billing_contact_state]
                                .filter(Boolean)
                                .join(', ')}
                              {opportunity.billing_contact_postal_code
                                ? ` ${opportunity.billing_contact_postal_code}`
                                : ''}
                              <br />
                            </span>
                          )}
                          {opportunity.billing_contact_country && (
                            <span>{opportunity.billing_contact_country}</span>
                          )}
                        </address>
                      ) : (
                        'N/A'
                      )}
                    </dd>
                  </div>
                )}

                {renderEditableField(
                  'PO Number',
                  'po_number__c',
                  opportunity.po_number__c || 'N/A',
                  'text'
                )}

                {renderEditableField(
                  'Invoice Memo',
                  'memo__c',
                  opportunity.memo__c || 'N/A',
                  'textarea'
                )}

                {isEditMode ? (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Contract Term</dt>
                    <dd className="mt-0.5">
                      <select
                        value={editForm.contract_term__c ? String(editForm.contract_term__c) : ''}
                        onChange={(e) =>
                          handleEditFieldChange(
                            'contract_term__c',
                            e.target.value ? parseInt(e.target.value) : undefined
                          )
                        }
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        aria-label="Contract term"
                      >
                        <option value="">Select...</option>
                        {CONTRACT_TERMS.map((term) => (
                          <option key={term} value={term}>
                            {CONTRACT_TERM_LABELS[term]}
                          </option>
                        ))}
                      </select>
                    </dd>
                  </div>
                ) : (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">Contract Term</dt>
                    <dd className="mt-0.5 text-sm">
                      {opportunity.contract_term__c
                        ? CONTRACT_TERM_LABELS[String(opportunity.contract_term__c)] ||
                          `${opportunity.contract_term__c} months`
                        : 'N/A'}
                    </dd>
                  </div>
                )}

                {renderEditableField(
                  'Contract Start Date',
                  'contract_start_date__c',
                  formatDate(opportunity.contract_start_date__c),
                  'date'
                )}

                {renderEditableField(
                  'Contract End Date',
                  'contract_end_date__c',
                  formatDate(opportunity.contract_end_date__c),
                  'date'
                )}

                {renderEditableField(
                  'Reason for Loss',
                  'reason_for_loss__c',
                  opportunity.reason_for_loss__c || 'N/A',
                  'select',
                  ['Other', 'Lost Interest', 'Budget', 'No Decision/ Unresponsive', 'Price']
                )}

                {renderEditableField(
                  'Competitor',
                  'competitor__c',
                  opportunity.competitor__c || 'N/A',
                  'text'
                )}

                {renderEditableField(
                  '# of Renewal Product',
                  'of_renewal_product__c',
                  opportunity.of_renewal_product__c ?? 'N/A',
                  'number'
                )}

                {/* Full-width textarea fields */}
                <div className="md:col-span-2">
                  {renderEditableField(
                    'Lost Reason Details',
                    'lost_reason_details__c',
                    opportunity.lost_reason_details__c || 'N/A',
                    'textarea'
                  )}
                </div>

                <div className="md:col-span-2">
                  {renderEditableField(
                    'Competitor Details',
                    'competitor_details__c',
                    opportunity.competitor_details__c || 'N/A',
                    'textarea'
                  )}
                </div>

                {/* Read-only metadata fields */}
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Created By</dt>
                  <dd className="mt-0.5 text-sm">
                    {opportunity.created_by_name || 'N/A'}
                    {opportunity.created_date && (
                      <span className="text-muted-foreground text-xs block mt-0.5 leading-tight">
                        {formatDateTime(opportunity.created_date)}
                      </span>
                    )}
                  </dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Last Modified By</dt>
                  <dd className="mt-0.5 text-sm">
                    {opportunity.last_modified_by_name || 'N/A'}
                    {opportunity.last_modified_date && (
                      <span className="text-muted-foreground text-xs block mt-0.5 leading-tight">
                        {formatDateTime(opportunity.last_modified_date)}
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
              {isEditMode && (
                <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={updateOpportunityMutation.isPending}
                    aria-label="Cancel editing"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    disabled={updateOpportunityMutation.isPending}
                    aria-label="Save opportunity changes"
                  >
                    {updateOpportunityMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Related Data Sidebar */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <Tabs
            value={searchParams.get('tab') || 'activity'}
            onValueChange={(t) =>
              setSearchParams(
                (prev) => {
                  prev.set('tab', t)
                  return prev
                },
                { replace: true }
              )
            }
            className="w-full"
          >
            <TabsList className="h-auto flex-wrap gap-1">
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="contacts">Contacts ({contact_roles.length})</TabsTrigger>
              <TabsTrigger value="emails">Emails</TabsTrigger>
              <TabsTrigger value="quotes">Quotes ({quotes.length})</TabsTrigger>
              <TabsTrigger value="products">Products ({products.length})</TabsTrigger>
              <TabsTrigger value="notes">
                Notes ({notes.length + (noteSessions?.length ?? 0)})
              </TabsTrigger>
              <TabsTrigger value="attachments">Attachments ({files.length})</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">Activity</h3>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCreateTaskModalOpen(true)}
                    aria-label="Create new task for this opportunity"
                  >
                    <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                    New Task
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCreateEventModalOpen(true)}
                    aria-label="Create new event for this opportunity"
                  >
                    <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                    New Event
                  </Button>
                </div>
              </div>
              <ActivityTimeline entityId={id!} entityType="Opportunity" showFilters={true} />
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts" className="space-y-4">
              {contact_roles.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Users className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
                    <p className="text-sm text-muted-foreground mb-4">
                      No contact roles for this opportunity yet.
                    </p>
                    {!isClosedWonLocked && (
                      <Button
                        onClick={() => setCreateContactRoleModalOpen(true)}
                        size="sm"
                        aria-label="Add first contact role to opportunity"
                      >
                        <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                        Add Contact
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Header with Add Button and Filter */}
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Contact Roles</h3>
                        {!isClosedWonLocked && (
                          <Button
                            onClick={() => setCreateContactRoleModalOpen(true)}
                            size="sm"
                            aria-label="Add contact role to opportunity"
                          >
                            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                            Add Contact
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <Filter className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                        <div className="flex-1">
                          <Label htmlFor="role-filter" className="text-sm font-medium">
                            Filter by Role
                          </Label>
                          <Select value={contactRoleFilter} onValueChange={setContactRoleFilter}>
                            <SelectTrigger
                              id="role-filter"
                              className="w-full mt-1"
                              aria-label="Filter contact roles by role type"
                            >
                              <SelectValue placeholder="All Roles" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">
                                All Roles ({contact_roles.length})
                              </SelectItem>
                              {availableRoles.map((role) => (
                                <SelectItem key={role} value={role}>
                                  {role} ({contact_roles.filter((cr) => cr.role === role).length})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {contactRoleFilter !== 'all' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setContactRoleFilter('all')}
                            aria-label="Clear role filter"
                            className="mt-6"
                          >
                            <X className="h-4 w-4 mr-1" aria-hidden="true" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <p
                        className="text-sm text-muted-foreground mt-2"
                        role="status"
                        aria-live="polite"
                      >
                        Showing {filteredContactRoles.length} of {contact_roles.length} contact
                        roles
                      </p>
                    </CardContent>
                  </Card>

                  {/* Contact Roles List */}
                  {filteredContactRoles.length === 0 ? (
                    <Card>
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <Users
                          className="h-12 w-12 text-muted-foreground mb-4"
                          aria-hidden="true"
                        />
                        <p className="text-sm text-muted-foreground">
                          No contact roles match the selected filter.
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    filteredContactRoles.map((contactRole) => (
                      <Card key={contactRole.id}>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="flex items-center gap-2">
                                {contactRole.contact_id ? (
                                  <Link
                                    to={`/contacts/${contactRole.contact_id}`}
                                    className="font-semibold text-base text-primary hover:underline"
                                    aria-label={`View contact: ${contactRole.contact_name || 'Unknown Contact'}`}
                                  >
                                    {contactRole.contact_name || 'Unknown Contact'}
                                  </Link>
                                ) : (
                                  <span>{contactRole.contact_name || 'Unknown Contact'}</span>
                                )}
                                {contactRole.is_primary && (
                                  <Badge variant="default" className="text-xs">
                                    Primary
                                  </Badge>
                                )}
                              </CardTitle>
                              {contactRole.contact_title && (
                                <CardDescription>{contactRole.contact_title}</CardDescription>
                              )}
                            </div>
                            <Badge variant="secondary">{contactRole.role}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <dl className="grid grid-cols-1 gap-3">
                            {contactRole.contact_email && (
                              <div className="flex items-center gap-2">
                                <Mail
                                  className="h-3.5 w-3.5 text-muted-foreground"
                                  aria-hidden="true"
                                />
                                <dd className="text-sm">
                                  <a
                                    href={`mailto:${contactRole.contact_email}`}
                                    className="text-primary hover:underline"
                                    aria-label={`Email ${contactRole.contact_name}`}
                                  >
                                    {contactRole.contact_email}
                                  </a>
                                </dd>
                              </div>
                            )}
                            {contactRole.contact_phone && (
                              <div className="flex items-center gap-2">
                                <User
                                  className="h-3.5 w-3.5 text-muted-foreground"
                                  aria-hidden="true"
                                />
                                <dd className="text-sm">
                                  <ClickToCallButton
                                    phoneNumber={contactRole.contact_phone}
                                    whoId={contactRole.contact_id}
                                    whoType="Contact"
                                    whoName={contactRole.contact_name}
                                    whatId={opportunity.id}
                                    whatType="Opportunity"
                                  />
                                </dd>
                              </div>
                            )}
                          </dl>
                          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                            {!isClosedWonLocked && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedContactRole(contactRole)
                                    setEditContactRoleModalOpen(true)
                                  }}
                                  aria-label={`Edit role for ${contactRole.contact_name}`}
                                >
                                  <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteContactRole(contactRole)}
                                  disabled={deleteContactRoleMutation.isPending}
                                  aria-label={`Remove ${contactRole.contact_name} from opportunity`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                                  Remove
                                </Button>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </>
              )}
            </TabsContent>

            {/* Emails Tab */}
            <TabsContent value="emails" className="space-y-4">
              <RelatedEmails entityType="opportunity" entityId={id!} maxHeight="500px" />
            </TabsContent>

            {/* Quotes Tab */}
            <TabsContent value="quotes" className="space-y-4">
              {!isClosedWonLocked && (
                <div className="flex justify-end mb-4">
                  <Button
                    onClick={handleCreateQuoteFromOpportunity}
                    disabled={createQuoteFromOppMutation.isPending}
                    size="sm"
                    aria-label="Create quote from this opportunity"
                  >
                    <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                    {createQuoteFromOppMutation.isPending ? 'Creating...' : 'Create Quote'}
                  </Button>
                </div>
              )}
              {quotes.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Package className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
                    <p className="text-sm text-muted-foreground">
                      No quotes for this opportunity yet.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                [...quotes]
                  .sort((a, b) => {
                    const aIsLive = a.id === opportunity.synced_quote_id ? -1 : 0
                    const bIsLive = b.id === opportunity.synced_quote_id ? -1 : 0
                    return aIsLive - bIsLive
                  })
                  .map((quote) => {
                    const isLiveQuote = quote.id === opportunity.synced_quote_id
                    return (
                      <Card
                        key={quote.id}
                        className={isLiveQuote ? 'border-l-4' : ''}
                        style={isLiveQuote ? { borderLeftColor: SEMANTIC.success.fill } : undefined}
                      >
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div>
                                <CardTitle>{quote.name}</CardTitle>
                                <CardDescription>Quote #{quote.quote_number}</CardDescription>
                              </div>
                              {isLiveQuote && (
                                <Badge
                                  variant="default"
                                  style={{
                                    backgroundColor: SEMANTIC.success.fill,
                                    color: SEMANTIC.success.text,
                                  }}
                                >
                                  <Check className="h-3 w-3 mr-1" aria-hidden="true" />
                                  Primary Quote
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge>{quote.status}</Badge>
                              {!isClosedWonLocked &&
                                (isLiveQuote ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleSetSyncedQuote(null)}
                                    disabled={setSyncedQuoteMutation.isPending}
                                    aria-label="Remove as primary quote"
                                  >
                                    {setSyncedQuoteMutation.isPending
                                      ? 'Updating...'
                                      : 'Remove as Primary'}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleSetSyncedQuote(quote.id)}
                                    disabled={
                                      setSyncedQuoteMutation.isPending ||
                                      quote.status === 'Rejected'
                                    }
                                    aria-label={`Set ${quote.name} as primary quote`}
                                  >
                                    {setSyncedQuoteMutation.isPending
                                      ? 'Updating...'
                                      : 'Set as Primary'}
                                  </Button>
                                ))}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/quotes/${quote.id}`)}
                                aria-label={`View quote ${quote.name}`}
                              >
                                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                              </Button>
                              {!isClosedWonLocked && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    setQuoteToDelete({ id: quote.id, name: quote.name })
                                  }
                                  className="text-muted-foreground hover:text-destructive"
                                  aria-label={`Delete quote ${quote.name}`}
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <dl className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <dt className="text-sm font-medium text-muted-foreground">Total</dt>
                              <dd className="mt-1 text-lg font-semibold">
                                {formatCurrency(quote.total_price)}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-muted-foreground">
                                Quote Date
                              </dt>
                              <dd className="mt-1 text-sm">{formatDate(quote.created_date)}</dd>
                            </div>
                          </dl>

                          {quote.line_items && quote.line_items.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">Line Items</h4>
                              <div className="border rounded-md">
                                <table className="w-full text-sm">
                                  <thead className="bg-muted/50">
                                    <tr>
                                      <th className="text-left p-2">Product</th>
                                      <th className="text-right p-2">Quantity</th>
                                      <th className="text-right p-2">Unit Price</th>
                                      <th className="text-right p-2">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {quote.line_items.map((item) => (
                                      <tr key={item.id} className="border-t">
                                        <td className="p-2">{item.product_name}</td>
                                        <td className="text-right p-2">{item.quantity}</td>
                                        <td className="text-right p-2">
                                          {formatCurrency(item.unit_price)}
                                        </td>
                                        <td className="text-right p-2">
                                          {formatCurrency(item.total_price)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )
                  })
              )}
            </TabsContent>

            {/* Products Tab */}
            <TabsContent value="products">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Line Items</CardTitle>
                    <CardDescription>Manage products for this opportunity</CardDescription>
                  </div>
                  {!isAddingLineItem && !isClosedWonLocked && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setAddProductsModalOpen(true)}
                        size="sm"
                        aria-label="Add products"
                      >
                        <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                        Add Product
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add/Edit Line Item Form */}
                  {isAddingLineItem && (
                    <div
                      className="border rounded-lg p-4 space-y-4 bg-muted/20"
                      role="form"
                      aria-label={editingLineItemId ? 'Edit line item' : 'Add new line item'}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-sm">
                          {editingLineItemId ? 'Edit Line Item' : 'Add New Line Item'}
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={resetLineItemForm}
                          aria-label="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Product Selection */}
                        <div className="space-y-2">
                          <Label htmlFor="product" className="required">
                            Product *
                          </Label>
                          <RecordCombobox
                            id="product"
                            records={availableProducts}
                            value={lineItemForm.product_id || ''}
                            onValueChange={handleProductSelect}
                            placeholder="Select a product"
                            searchPlaceholder="Search products..."
                            emptyMessage="No products found."
                            displayField={(p) =>
                              `${p.name}${p.product_code ? ` (${p.product_code})` : ''}`
                            }
                            aria-label="Select product for line item"
                            onSearchChange={setProductSearch}
                            isLoading={productsLoading}
                            totalCount={productsData?.total}
                          />
                          {formErrors.product_id && (
                            <p id="product-error" className="text-sm text-destructive" role="alert">
                              {formErrors.product_id}
                            </p>
                          )}
                        </div>

                        {/* Quantity */}
                        <div className="space-y-2">
                          <Label htmlFor="quantity" className="required">
                            Quantity *
                          </Label>
                          <Input
                            id="quantity"
                            type="number"
                            min="1"
                            step="1"
                            value={lineItemForm.quantity || ''}
                            onChange={(e) => {
                              setLineItemForm((prev) => ({
                                ...prev,
                                quantity: Number(e.target.value),
                              }))
                              if (formErrors.quantity) {
                                setFormErrors((prev) => {
                                  const newErrors = { ...prev }
                                  delete newErrors.quantity
                                  return newErrors
                                })
                              }
                            }}
                            aria-required="true"
                            aria-invalid={!!formErrors.quantity}
                            aria-describedby={formErrors.quantity ? 'quantity-error' : undefined}
                          />
                          {formErrors.quantity && (
                            <p
                              id="quantity-error"
                              className="text-sm text-destructive"
                              role="alert"
                            >
                              {formErrors.quantity}
                            </p>
                          )}
                        </div>

                        {/* Unit Price */}
                        <div className="space-y-2">
                          <Label htmlFor="unit_price" className="required">
                            Unit Price *
                          </Label>
                          <Input
                            id="unit_price"
                            type="number"
                            min="0"
                            step="0.01"
                            value={lineItemForm.unit_price || ''}
                            onChange={(e) => {
                              setLineItemForm((prev) => ({
                                ...prev,
                                unit_price: Number(e.target.value),
                              }))
                              if (formErrors.unit_price) {
                                setFormErrors((prev) => {
                                  const newErrors = { ...prev }
                                  delete newErrors.unit_price
                                  return newErrors
                                })
                              }
                            }}
                            aria-required="true"
                            aria-invalid={!!formErrors.unit_price}
                            aria-describedby={
                              formErrors.unit_price ? 'unit_price-error' : undefined
                            }
                          />
                          {formErrors.unit_price && (
                            <p
                              id="unit_price-error"
                              className="text-sm text-destructive"
                              role="alert"
                            >
                              {formErrors.unit_price}
                            </p>
                          )}
                        </div>

                        {/* Discount */}
                        <div className="space-y-2">
                          <Label htmlFor="discount">Discount ($)</Label>
                          <Input
                            id="discount"
                            type="number"
                            min="0"
                            step="0.01"
                            value={lineItemForm.discount || 0}
                            onChange={(e) => {
                              setLineItemForm((prev) => ({
                                ...prev,
                                discount: Number(e.target.value),
                              }))
                              if (formErrors.discount) {
                                setFormErrors((prev) => {
                                  const newErrors = { ...prev }
                                  delete newErrors.discount
                                  return newErrors
                                })
                              }
                            }}
                            aria-invalid={!!formErrors.discount}
                            aria-describedby={formErrors.discount ? 'discount-error' : undefined}
                          />
                          {formErrors.discount && (
                            <p
                              id="discount-error"
                              className="text-sm text-destructive"
                              role="alert"
                            >
                              {formErrors.discount}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Discount description — why this line item is discounted */}
                      <div className="space-y-2">
                        <Label htmlFor="discount_description">Discount description</Label>
                        <Textarea
                          id="discount_description"
                          rows={2}
                          placeholder="Why this line item is discounted (optional)…"
                          value={lineItemForm.discount_description || ''}
                          onChange={(e) =>
                            setLineItemForm((prev) => ({
                              ...prev,
                              discount_description: e.target.value,
                            }))
                          }
                        />
                      </div>

                      {/* Calculated Total */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-sm font-medium">Line Total:</span>
                        <span className="text-lg font-semibold">
                          {formatCurrency(
                            calculateLineItemTotal(
                              lineItemForm.quantity || 0,
                              lineItemForm.unit_price || 0,
                              lineItemForm.discount || 0
                            )
                          )}
                        </span>
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={resetLineItemForm}
                          disabled={
                            createLineItemMutation.isPending || updateLineItemMutation.isPending
                          }
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={() => handleSaveLineItem()}
                          disabled={
                            createLineItemMutation.isPending || updateLineItemMutation.isPending
                          }
                        >
                          {createLineItemMutation.isPending || updateLineItemMutation.isPending
                            ? 'Saving...'
                            : editingLineItemId
                              ? 'Update'
                              : 'Add'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Line Items Table */}
                  {lineItemsLoading ? (
                    <div
                      className="flex items-center justify-center py-8"
                      role="status"
                      aria-live="polite"
                    >
                      <div
                        className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"
                        role="progressbar"
                        aria-label="Loading line items"
                      ></div>
                      <span className="ml-2 text-sm text-muted-foreground">
                        Loading line items...
                      </span>
                    </div>
                  ) : lineItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 border rounded-lg">
                      <Package
                        className="h-12 w-12 text-muted-foreground mb-4"
                        aria-hidden="true"
                      />
                      <p className="text-sm text-muted-foreground">No line items added yet.</p>
                      {!isAddingLineItem && !isClosedWonLocked && (
                        <div className="flex gap-2 mt-4">
                          <Button
                            onClick={() => setAddProductsModalOpen(true)}
                            variant="outline"
                            aria-label="Add products"
                          >
                            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                            Add Product
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border rounded-md">
                      <table
                        className="w-full text-sm"
                        role="table"
                        aria-label="Opportunity line items"
                      >
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3" scope="col">
                              Product
                            </th>
                            <th className="text-right p-3" scope="col">
                              Quantity
                            </th>
                            <th className="text-right p-3" scope="col">
                              Unit Price
                            </th>
                            <th className="text-right p-3" scope="col">
                              Total
                            </th>
                            <th className="text-right p-3" scope="col">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.map((item, index) => {
                            const subtotal = item.quantity * item.unit_price
                            const discountAmount = item.discount || 0
                            return (
                              <Fragment key={item.id}>
                                {/* Product row at full price */}
                                <tr className="border-t">
                                  <td className="p-3">
                                    <div>
                                      <div className="font-medium">
                                        {item.product_name || 'Unknown Product'}
                                      </div>
                                      {item.product_code && (
                                        <div className="text-xs text-muted-foreground">
                                          {item.product_code}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="text-right p-3">{item.quantity}</td>
                                  <td className="text-right p-3">
                                    {formatCurrency(item.unit_price)}
                                  </td>
                                  <td className="text-right p-3 font-medium">
                                    {formatCurrency(subtotal)}
                                  </td>
                                  <td className="text-right p-3">
                                    <div className="flex items-center justify-end gap-2">
                                      {lineItems.length > 1 && (
                                        <>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => handleMoveLineItem(index, 'up')}
                                            disabled={
                                              index === 0 ||
                                              reorderLineItemsMutation.isPending ||
                                              isClosedWonLocked
                                            }
                                            aria-label={`Move ${item.product_name || 'product'} up`}
                                          >
                                            <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => handleMoveLineItem(index, 'down')}
                                            disabled={
                                              index === lineItems.length - 1 ||
                                              reorderLineItemsMutation.isPending ||
                                              isClosedWonLocked
                                            }
                                            aria-label={`Move ${item.product_name || 'product'} down`}
                                          >
                                            <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                                          </Button>
                                        </>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditLineItem(item)}
                                        disabled={isAddingLineItem || isClosedWonLocked}
                                        aria-label={`Edit line item for ${item.product_name}`}
                                      >
                                        <Pencil className="h-4 w-4" aria-hidden="true" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteLineItem(item.id)}
                                        disabled={
                                          deleteLineItemMutation.isPending || isClosedWonLocked
                                        }
                                        aria-label={`Delete line item for ${item.product_name}`}
                                      >
                                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                                {/* Approval row — shown for a discount OR a Workspace
                                    renewal under 10% (which may carry no discount).
                                    The approved-with-an-approver case catches a
                                    renewal price that someone actually signed off:
                                    it carries no discount, so without this the row
                                    (and its revert control) would vanish. An
                                    ordinary line sits on the legacy 'approved'
                                    default with no approver, and stays hidden. */}
                                {showsApprovalRow(item) && (
                                  <tr
                                    style={{
                                      // An approved line gets no tint at all — it
                                      // inherits the table background, so it reads as
                                      // settled and the badge carries the state. Only
                                      // undecided (warning) and rejected (error) rows
                                      // are tinted.
                                      backgroundColor:
                                        (item as any).discount_status === 'pending' ||
                                        (item as any).discount_status === 'endorsed'
                                          ? `${SEMANTIC.warning.fill}80`
                                          : (item as any).discount_status === 'rejected'
                                            ? `${SEMANTIC.error.fill}40`
                                            : undefined,
                                    }}
                                  >
                                    <td className="p-3 pl-8 text-sm text-destructive">
                                      <span className="flex items-center gap-2">
                                        {Number(item.discount) > 0 ? 'Discount' : 'Renewal price'}
                                        {(item as any).discount_status === 'pending' && (
                                          <Badge variant="warning" className="text-[10px]">
                                            Pending Approval
                                          </Badge>
                                        )}
                                        {(item as any).discount_status === 'endorsed' && (
                                          <Badge variant="warning" className="text-[10px]">
                                            Endorsed – awaiting Super Admin
                                          </Badge>
                                        )}
                                        {(item as any).discount_status === 'rejected' && (
                                          <Badge variant="destructive" className="text-[10px]">
                                            Rejected
                                          </Badge>
                                        )}
                                        {(item as any).discount_status === 'approved' &&
                                          (item as any).discount_approved_by && (
                                            <Badge variant="success" className="text-[10px]">
                                              Approved
                                            </Badge>
                                          )}
                                      </span>
                                      {item.discount_description && (
                                        <p className="mt-0.5 text-xs font-normal text-muted-foreground">
                                          {item.discount_description}
                                        </p>
                                      )}
                                      {(item as any).renewal_justification && (
                                        <p className="mt-0.5 text-xs font-normal text-muted-foreground">
                                          <span className="font-medium">
                                            Renewal justification:
                                          </span>{' '}
                                          {(item as any).renewal_justification}
                                        </p>
                                      )}
                                      {/* Who decided this, when, and at which authority
                                          level — so an approved line is unambiguous. */}
                                      {(item as any).discount_status === 'approved' &&
                                        (item as any).discount_approved_by && (
                                          <p className="mt-0.5 text-xs font-normal text-muted-foreground">
                                            Approved by{' '}
                                            {(item as any).discount_approved_by_name ||
                                              'an administrator'}{' '}
                                            on{' '}
                                            {formatDateTime((item as any).discount_approval_date)} ·{' '}
                                            {approvalTierLabel(item)}
                                            {(item as any).discount_endorsed_by_name &&
                                              ` · Endorsed by ${(item as any).discount_endorsed_by_name}`}
                                          </p>
                                        )}
                                      {(item as any).discount_status === 'endorsed' && (
                                        <p className="mt-0.5 text-xs font-normal text-muted-foreground">
                                          Endorsed by{' '}
                                          {(item as any).discount_endorsed_by_name ||
                                            'an administrator'}{' '}
                                          on {formatDateTime((item as any).discount_endorsed_date)}{' '}
                                          · awaiting Super Admin final approval
                                        </p>
                                      )}
                                      {(item as any).discount_status === 'rejected' && (
                                        <p className="mt-0.5 text-xs font-normal text-muted-foreground">
                                          Rejected by{' '}
                                          {(item as any).discount_approved_by_name ||
                                            'an administrator'}{' '}
                                          on {formatDateTime((item as any).discount_approval_date)}
                                        </p>
                                      )}
                                    </td>
                                    <td className="text-right p-3"></td>
                                    <td className="text-right p-3"></td>
                                    <td className="text-right p-3 font-medium text-destructive">
                                      {Number(item.discount) > 0
                                        ? `-${formatCurrency(discountAmount)}`
                                        : ''}
                                    </td>
                                    <td className="text-right p-3">
                                      {canActOnItem(item) && (
                                        <div className="flex gap-1 justify-end">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-xs"
                                            style={{ color: GRACKLE_COLORS.navy }}
                                            disabled={discountActionInFlight}
                                            onClick={() =>
                                              handleApproveDiscount(item.id, 'approve')
                                            }
                                            aria-label={
                                              isFirstPass(item)
                                                ? `Endorse discount on ${item.product_name || 'this line item'}`
                                                : `Approve discount on ${item.product_name || 'this line item'}`
                                            }
                                          >
                                            {isFirstPass(item) ? 'Endorse' : 'Approve'}
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-xs text-destructive"
                                            disabled={discountActionInFlight}
                                            onClick={() => handleApproveDiscount(item.id, 'reject')}
                                            aria-label={`Reject discount on ${item.product_name || 'this line item'}`}
                                          >
                                            Reject
                                          </Button>
                                        </div>
                                      )}
                                      {canRevertItem(item) && (
                                        <div className="flex justify-end">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-xs"
                                            disabled={discountActionInFlight}
                                            onClick={() => setDiscountToRevert(item)}
                                            aria-label={
                                              (item as any).discount_status === 'rejected'
                                                ? `Reverse rejection of discount on ${item.product_name || 'this line item'}`
                                                : (item as any).discount_status === 'endorsed'
                                                  ? `Withdraw endorsement of discount on ${item.product_name || 'this line item'}`
                                                  : `Reverse approval of discount on ${item.product_name || 'this line item'}`
                                            }
                                          >
                                            <Undo2 className="mr-1 h-3 w-3" aria-hidden="true" />
                                            {(item as any).discount_status === 'rejected'
                                              ? 'Reverse rejection'
                                              : (item as any).discount_status === 'endorsed'
                                                ? 'Withdraw endorsement'
                                                : 'Revert approval'}
                                          </Button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            )
                          })}
                          <tr className="border-t bg-muted/30 font-semibold">
                            <td colSpan={3} className="text-right p-3">
                              Subtotal:
                            </td>
                            <td className="text-right p-3">{formatCurrency(lineItemsSubtotal)}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">Notes</h3>
                <Button
                  size="sm"
                  onClick={() => {
                    setExistingSessionId(undefined)
                    setNotePanelOpen(true)
                  }}
                  aria-label="Add note"
                >
                  <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                  Add Note
                </Button>
              </div>
              <MergedNotesList
                entityType="Opportunity"
                entityId={opportunity.id}
                legacyNotes={notes}
                currentUserId={currentUser?.id}
                currentUserType={currentUser?.user_type}
                onOpenSession={(sessionId) => {
                  setExistingSessionId(sessionId)
                  setNotePanelOpen(true)
                }}
                onOpenLegacyNote={(note) => {
                  setSelectedNoteId(note.id)
                  setNoteDetailModalOpen(true)
                }}
                onEditNote={(note) => {
                  setEditingNote(note)
                  setEditNoteModalOpen(true)
                }}
                onDeleteNote={(note) => {
                  deleteNoteMutation.mutate(note.id)
                }}
                onEditSession={(session) => {
                  setEditingSession(session)
                  setExistingSessionId(session.id)
                  setNotePanelOpen(true)
                }}
                onDeleteSession={(session) => {
                  deleteSessionMutation.mutate(session.id)
                }}
              />
            </TabsContent>

            {/* Attachments Tab */}
            <TabsContent value="attachments" className="space-y-4">
              <AttachmentsTab entityId={id!} entityType="Opportunity" />
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="space-y-4">
              <HistoryTimeline entityId={id!} entityType="Opportunity" showFilters={true} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <EditActionBar
        isEditMode={isEditMode}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        isSaving={updateOpportunityMutation.isPending}
        saveLabel="Save Changes"
      />

      {/* Modals */}
      <CreateOpportunityContactRoleModal
        opportunityId={id!}
        accountId={data?.opportunity?.account_id}
        open={createContactRoleModalOpen}
        onOpenChange={setCreateContactRoleModalOpen}
      />
      <EditOpportunityContactRoleModal
        opportunityId={id!}
        contactRole={selectedContactRole}
        open={editContactRoleModalOpen}
        onOpenChange={setEditContactRoleModalOpen}
      />
      <NoteDetailModal
        noteId={selectedNoteId}
        open={noteDetailModalOpen}
        onOpenChange={setNoteDetailModalOpen}
        currentUserId={currentUser?.id}
        currentUserType={currentUser?.user_type}
        onEdit={(note) => {
          setSelectedNoteId(null)
          setNoteDetailModalOpen(false)
          setEditingNote(note)
          setEditNoteModalOpen(true)
        }}
        onDelete={(note) => {
          deleteNoteMutation.mutate(note.id)
          setNoteDetailModalOpen(false)
        }}
      />

      {/* Edit Note Modal */}
      <EditNoteModal
        open={editNoteModalOpen}
        onOpenChange={setEditNoteModalOpen}
        note={editingNote}
        parentId={id!}
      />

      {/* Create Billing Contact Modal */}
      <CreateContactModal
        open={createBillingContactModalOpen}
        onOpenChange={setCreateBillingContactModalOpen}
        accountId={opportunity.account_id}
        ownerId={opportunity.owner_id}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        open={createTaskModalOpen}
        onOpenChange={setCreateTaskModalOpen}
        relatedToId={id}
        relatedToType="Opportunity"
        relatedToName={opportunity.opportunity_name}
        ownerId={opportunity.owner_id}
      />

      {/* Create Event Modal */}
      <CreateEventModal
        open={createEventModalOpen}
        onOpenChange={setCreateEventModalOpen}
        relatedToId={id}
        relatedToType="Opportunity"
        relatedToName={opportunity.opportunity_name}
        ownerId={opportunity.owner_id}
      />

      {/* Schedule Meet Dialog */}
      <ScheduleMeetDialog
        open={showMeetDialog}
        onOpenChange={setShowMeetDialog}
        entityType="opportunity"
        entityId={id!}
        defaultTitle={`Meeting: ${opportunity.opportunity_name || 'Opportunity'}`}
        defaultAttendees={contact_roles
          .filter((cr) => cr.contact_email)
          .slice(0, 5)
          .map((cr) => cr.contact_email!)}
      />

      {/* Renewal Opportunity Created Dialog */}
      <Dialog open={!!renewalCreated} onOpenChange={() => setRenewalCreated(null)}>
        <DialogContent aria-describedby="renewal-description">
          <DialogHeader>
            <DialogTitle>Renewal Opportunity Created</DialogTitle>
            <DialogDescription id="renewal-description">
              A renewal opportunity has been automatically created based on the contract end date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{renewalCreated?.name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Close date set to contract end date. Line items and contract term have been copied.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRenewalCreated(null)}>
                Dismiss
              </Button>
              <Button
                onClick={() => {
                  if (renewalCreated) navigate(`/opportunities/${renewalCreated.id}`)
                  setRenewalCreated(null)
                }}
              >
                View Renewal Opportunity
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Workspace renewal justification prompt — shown when the backend blocks a
          sub-10%-increase Workspace renewal until the rep explains it. */}
      <Dialog
        open={showRenewalModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowRenewalModal(false)
            setRenewalJustification('')
            setRenewalInfo(null)
          }
        }}
      >
        <DialogContent aria-describedby="renewal-justification-desc">
          <DialogHeader>
            <DialogTitle>
              {renewalInfo?.currencyChanged
                ? 'Workspace renewal currency changed'
                : 'Workspace renewal below a 10% increase'}
            </DialogTitle>
            <DialogDescription id="renewal-justification-desc">
              {renewalInfo
                ? renewalInfo.currencyChanged
                  ? `This renewal is in ${renewalInfo.currentCurrency} but last year's Closed-Won deal was in ${renewalInfo.priorCurrency} (converted: CA$${Math.round(
                      renewalInfo.current
                    ).toLocaleString('en-US')} vs CA$${Math.round(renewalInfo.prior).toLocaleString(
                      'en-US'
                    )}). A currency change requires approval.`
                  : `This renewal is ${
                      renewalInfo.pct === null
                        ? 'below last year'
                        : `${renewalInfo.pct >= 0 ? '+' : ''}${renewalInfo.pct.toFixed(1)}% vs last year`
                    } (CA$${Math.round(renewalInfo.current).toLocaleString(
                      'en-US'
                    )} vs CA$${Math.round(renewalInfo.prior).toLocaleString(
                      'en-US'
                    )}). Renewals should increase by at least 10%.`
                : 'This renewal is below the required 10% increase.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="renewal-justification">
              {renewalInfo?.currencyChanged
                ? 'Explain the currency change and confirm the renewal price'
                : "Explain the steps you've taken and why a 10% increase isn't possible"}
            </Label>
            <Textarea
              id="renewal-justification"
              rows={4}
              value={renewalJustification}
              onChange={(e) => setRenewalJustification(e.target.value)}
              placeholder="e.g. Customer budget frozen for FY; secured a multi-year commitment instead; competitive pressure from…"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowRenewalModal(false)
                setRenewalJustification('')
                setRenewalInfo(null)
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRenewalJustification}
              disabled={!renewalJustification.trim()}
            >
              Submit for approval
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AddProductsModal
        open={addProductsModalOpen}
        onOpenChange={setAddProductsModalOpen}
        opportunityId={id!}
        currency={opportunity.currency_iso_code || 'CAD'}
        existingProductIds={lineItems.map((li: any) => li.product_id).filter(Boolean)}
      />

      <ClosedLostModal
        open={showClosedLostModal}
        onOpenChange={setShowClosedLostModal}
        onConfirm={handleClosedLostConfirm}
        isPending={updateOpportunityMutation.isPending}
      />

      {/* Closed Won Celebration */}
      {showCelebration && (
        <ClosedWonCelebration
          opportunityName={opportunity.opportunity_name || 'Opportunity'}
          amount={opportunity.amount || 0}
          onComplete={handleCelebrationComplete}
        />
      )}

      {/* Note Session Panel */}
      <NoteSessionPanel
        entityType="Opportunity"
        entityId={opportunity.id}
        entityName={opportunity.opportunity_name}
        open={notePanelOpen}
        onOpenChange={(open) => {
          setNotePanelOpen(open)
          if (!open) {
            setExistingSessionId(undefined)
            setEditingSession(null)
          }
        }}
        readOnly={!!impersonatedUser}
        existingSessionId={existingSessionId}
        allowEdit={editingSession?.id === existingSessionId}
      />

      {/* Delete Opportunity confirmation (Admin/Super Admin only) */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-opportunity-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Opportunity</AlertDialogTitle>
            <AlertDialogDescription id="delete-opportunity-description">
              Are you sure you want to delete the opportunity "{opportunity.opportunity_name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteOpportunityMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm delete opportunity ${opportunity.opportunity_name}`}
            >
              {deleteOpportunityMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Quote confirmation */}
      <AlertDialog
        open={quoteToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setQuoteToDelete(null)
        }}
      >
        <AlertDialogContent aria-describedby="delete-quote-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quote</AlertDialogTitle>
            <AlertDialogDescription id="delete-quote-description">
              Are you sure you want to delete the quote "{quoteToDelete?.name}"? Its line items will
              be removed and, if this is the primary quote, it will be unlinked from the
              opportunity. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete quote">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuoteConfirm}
              disabled={deleteQuoteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm delete quote ${quoteToDelete?.name ?? ''}`}
            >
              {deleteQuoteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reverse a discount decision — confirmed because it re-opens the line for
          approval, re-notifies the approvers, and (for a reversed rejection)
          cannot restore the discount amount that rejection removed. */}
      <AlertDialog
        open={discountToRevert !== null}
        onOpenChange={(open) => {
          if (!open) setDiscountToRevert(null)
        }}
      >
        <AlertDialogContent aria-describedby="revert-discount-description">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {discountToRevert?.discount_status === 'rejected'
                ? 'Reverse this rejection?'
                : discountToRevert?.discount_status === 'endorsed'
                  ? 'Withdraw this endorsement?'
                  : 'Reverse this approval?'}
            </AlertDialogTitle>
            <AlertDialogDescription id="revert-discount-description">
              This returns the discount on {discountToRevert?.product_name || 'this line item'} to
              Pending Approval and notifies the approvers again. A discount over 10% will need Admin
              endorsement and Super Admin final approval again, and quotes cannot be generated until
              it is approved.
              {discountToRevert?.discount_status === 'rejected' && (
                <>
                  {' '}
                  <strong>
                    Rejecting removed the discount amount, so reversing cannot restore it — the
                    discount will need to be re-entered.
                  </strong>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel reversing the discount decision">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Keep the dialog mounted until the request resolves so the
                // spinner is visible and a failure can be reported in place.
                e.preventDefault()
                if (discountToRevert) handleRevertDiscount(discountToRevert)
              }}
              disabled={revertDiscountMutation.isPending}
              aria-label={
                discountToRevert?.discount_status === 'rejected'
                  ? 'Confirm reversing the rejection'
                  : discountToRevert?.discount_status === 'endorsed'
                    ? 'Confirm withdrawing the endorsement'
                    : 'Confirm reversing the approval'
              }
            >
              {revertDiscountMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {discountToRevert?.discount_status === 'rejected'
                ? 'Reverse rejection'
                : discountToRevert?.discount_status === 'endorsed'
                  ? 'Withdraw endorsement'
                  : 'Revert approval'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
